import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useRole } from '@/contexts/RoleContext';
import { supabase, executeWithRetry } from '@/lib/supabase';
import { CLEANING_STAFF_ROLES } from '@/lib/cleaningStaffRoles';
import {
  grantStaffLocationAccess,
  isAssignmentPlaceholderSection,
  revokeStaffLocationAccess,
} from '@/lib/locationAccess';
import { CleaningIssueHistoryTable } from '@/components/admin/CleaningIssueHistoryTable';
import { ContractorAttachCard } from '@/components/legal-entity/ContractorAttachCard';
import { HOUSING_KINDS, COMPANY_KINDS } from '@/lib/legalEntityMessages';
import type { CleaningIssueHistoryRow } from '@/lib/issueStatusLabels';
import { toast } from 'sonner';
import { Database } from '@/integrations/supabase/types';
import { asJson, firstJoined, isPlainObject, parseChecklistItems } from '@/lib/checklistJson';
import { StoragePhotoLink } from '@/components/StoragePhoto';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  ArrowLeft,
  ArrowUp,
  ArrowDown,
  MapPin,
  ExternalLink,
  Plus,
  Edit,
  User,
  Mail,
  Phone,
  Building,
  Loader2,
  Save,
  X,
  MoreVertical,
  ChevronUp,
  ChevronDown,
  Trash2,
  Calendar as CalendarIcon,
  Clock,
  Settings,
  Pause,
  Play,
  Zap,
  Package,
  FileText,
  Download,
  QrCode,
  Printer,
  RefreshCw,
  Camera,
  RotateCcw,
  Lock,
  MessageSquare,
  Wallet,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { VirtualizedColumn } from '@/components/ui/virtualized-column';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { format } from 'date-fns';
import { dateFnsLocaleFor, intlLocaleFor } from '@/lib/appLocale';
import { QRCodeSVG } from 'qrcode.react';
import {
  ToggleGroup,
  ToggleGroupItem,
} from '@/components/ui/toggle-group';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { SearchableSelect } from '@/components/admin/SearchableSelect';
import { ExtraPaidJobModal } from '@/components/admin/ExtraPaidJobModal';
import { generateTasksForTemplate, calculateNextExecutionDate } from '@/lib/taskGeneratorService';

type CleaningLocation = Database['public']['Tables']['cleaning_locations']['Row'];
type CleaningClient = Database['public']['Tables']['cleaning_clients']['Row'];
type PropertySection = Database['public']['Tables']['property_sections']['Row'] & {
  sort_order?: number | null;
};
type PropertyChecklist = Database['public']['Tables']['property_checklists']['Row'] & {
  property_sections?: {
    name: string;
  } | null;
};
type CleaningTask = Database['public']['Tables']['cleaning_tasks']['Row'] & {
  property_sections?: {
    name: string;
  } | null;
  profiles?: { full_name: string | null } | { full_name: string | null }[] | null;
  assignedStaffName?: string | null;
  actual_performer_id?: string | null;
  performerName?: string | null;
};

interface ValidationConfig {
  qr_required?: boolean;
  gps_required?: boolean;
  biometric_required?: boolean;
}

interface ContractDetails {
  nip?: string;
  address?: string;
}

type StaffListPerson = {
  id: string;
  full_name: string | null;
  status?: string;
  role?: string;
};

type AvailablePerson = {
  id: string;
  full_name: string | null;
};

interface LocationWithClient extends CleaningLocation {
  cleaning_clients?: CleaningClient | CleaningClient[] | null;
}

function parseFrequencyConfig(value: unknown): { type: string; days?: number[]; text?: string } | null {
  if (!isPlainObject(value) || typeof value.type !== 'string') return null;
  return {
    type: value.type,
    days: Array.isArray(value.days)
      ? value.days.filter((day): day is number => typeof day === 'number')
      : undefined,
    text: typeof value.text === 'string' ? value.text : undefined,
  };
}

export const PropertyDetailsPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const dateLocale = dateFnsLocaleFor(i18n.language);
  const intlLocale = intlLocaleFor(i18n.language);
  const { currentRole, currentOrgId } = useRole();

  const [location, setLocation] = useState<LocationWithClient | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadError, setIsLoadError] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [validationConfig, setValidationConfig] = useState<ValidationConfig>({
    qr_required: false,
    gps_required: false,
    biometric_required: false,
  });

  // Edit states
  const [isEditingClient, setIsEditingClient] = useState(false);
  const [isEditingInstructions, setIsEditingInstructions] = useState(false);
  const [editedClientData, setEditedClientData] = useState<{
    name: string;
    email: string;
    phone: string;
    nip: string;
    address: string;
  }>({
    name: '',
    email: '',
    phone: '',
    nip: '',
    address: '',
  });
  const [editedInstructions, setEditedInstructions] = useState<{
    access_code: string;
    instruction_notes: string;
    coordinator_notes: string;
  }>({
    access_code: '',
    instruction_notes: '',
    coordinator_notes: '',
  });

  // Sections and Personnel states
  const [sections, setSections] = useState<PropertySection[]>([]);
  const [checklists, setChecklists] = useState<PropertyChecklist[]>([]);
  const [personnel, setPersonnel] = useState<StaffListPerson[]>([]);
  const [isLoadingSections, setIsLoadingSections] = useState(true);
  const [showArchivedSections, setShowArchivedSections] = useState(false);
  const [isLoadingPersonnel, setIsLoadingPersonnel] = useState(false);
  const [isAddSectionDialogOpen, setIsAddSectionDialogOpen] = useState(false);
  const [isAssignPersonnelDialogOpen, setIsAssignPersonnelDialogOpen] = useState(false);
  const [isAddTaskDialogOpen, setIsAddTaskDialogOpen] = useState(false);
  const [isEditSectionDialogOpen, setIsEditSectionDialogOpen] = useState(false);
  const [newSectionName, setNewSectionName] = useState('');
  const [editingSection, setEditingSection] = useState<PropertySection | null>(null);
  const [selectedSectionForTask, setSelectedSectionForTask] = useState<string>('');
  const [newTaskName, setNewTaskName] = useState<string>('');
  const [newTaskFrequencyType, setNewTaskFrequencyType] = useState<string>('daily');
  const [selectedDays, setSelectedDays] = useState<string[]>([]);
  const [newTaskRequiresPhoto, setNewTaskRequiresPhoto] = useState<boolean>(false);
  const [availablePersonnel, setAvailablePersonnel] = useState<AvailablePerson[]>([]);
  const [isLoadingAvailablePersonnel, setIsLoadingAvailablePersonnel] = useState(false);
  const [selectedPersonnelId, setSelectedPersonnelId] = useState<string>('');
  const [personnelToDelete, setPersonnelToDelete] = useState<string | null>(null);

  // Schedule/Harmonogram states
  const [scheduleChecklists, setScheduleChecklists] = useState<PropertyChecklist[]>([]);
  const [isLoadingSchedule, setIsLoadingSchedule] = useState(false);
  const [scheduleSortConfig, setScheduleSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({ key: '', direction: 'asc' });

  // Single task states (coordinator_single)
  const [isAddSingleTaskDialogOpen, setIsAddSingleTaskDialogOpen] = useState(false);
  const [isExtraPaidOpen, setIsExtraPaidOpen] = useState(false);
  const [singleTaskSectionId, setSingleTaskSectionId] = useState<string>('');
  const [singleTaskName, setSingleTaskName] = useState<string>('');
  const [singleTaskRequiresPhoto, setSingleTaskRequiresPhoto] = useState<boolean>(false);

  // Global org setting: coordinators can view financials (from AdminDashboard switch)
  const [globalCoordinatorViewFinancials, setGlobalCoordinatorViewFinancials] = useState(false);

  // Financial data states
  const [isEditingFinancials, setIsEditingFinancials] = useState(false);
  const [editedFinancialData, setEditedFinancialData] = useState<{
    monthly_salary: number;
    cleaning_rate: number;
    snow_removal_rate: number;
    extra_work_rate: number;
    coordinator_can_view_financials: boolean;
  }>({
    monthly_salary: 0,
    cleaning_rate: 0,
    snow_removal_rate: 0,
    extra_work_rate: 0,
    coordinator_can_view_financials: false,
  });

  // Current view tasks states
  const [currentViewTasks, setCurrentViewTasks] = useState<CleaningTask[]>([]);
  const [isLoadingCurrentViewTasks, setIsLoadingCurrentViewTasks] = useState(false);
  const [selectedTaskDetails, setSelectedTaskDetails] = useState<CleaningTask | null>(null);
  const [isTaskDetailsDialogOpen, setIsTaskDetailsDialogOpen] = useState(false);
  const [taskDetailsPerformerName, setTaskDetailsPerformerName] = useState<string | null>(null);
  const [taskToDelete, setTaskToDelete] = useState<CleaningTask | null>(null);

  // QR Code Management states
  const [isQRManagementDialogOpen, setIsQRManagementDialogOpen] = useState(false);
  const [isRegeneratingQR, setIsRegeneratingQR] = useState(false);


  // Warehouse and History states
  const [materialRequests, setMaterialRequests] = useState<any[]>([]);
  const [isLoadingMaterialRequests, setIsLoadingMaterialRequests] = useState(false);

  // Holidays states
  const [holidays, setHolidays] = useState<Array<{
    id: string;
    org_id: string;
    location_id: string | null;
    holiday_date: string;
    description: string | null;
    created_at: string;
    updated_at: string;
  }>>([]);
  const [isLoadingHolidays, setIsLoadingHolidays] = useState(false);
  const [isAddHolidayDialogOpen, setIsAddHolidayDialogOpen] = useState(false);
  const [newHolidayDate, setNewHolidayDate] = useState<Date | undefined>(undefined);
  const [newHolidayDescription, setNewHolidayDescription] = useState('');
  const [isGlobalHoliday, setIsGlobalHoliday] = useState(false);
  const [holidayToDelete, setHolidayToDelete] = useState<string | null>(null);
  const [completedTasks, setCompletedTasks] = useState<CleaningTask[]>([]);
  const [isLoadingCompletedTasks, setIsLoadingCompletedTasks] = useState(false);
  const [isLoadingMoreHistory, setIsLoadingMoreHistory] = useState(false);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyHasMore, setHistoryHasMore] = useState(true);
  const HISTORY_ITEMS_PER_PAGE = 30;
  const [historyDateRange, setHistoryDateRange] = useState<{ from?: Date; to?: Date }>({});
  const [historyStaffFilter, setHistoryStaffFilter] = useState<string>('all');
  const [issuesHistory, setIssuesHistory] = useState<CleaningIssueHistoryRow[]>([]);
  const [issuesHistoryDateRange, setIssuesHistoryDateRange] = useState<{ from?: Date; to?: Date }>({});
  const [isLoadingIssuesHistory, setIsLoadingIssuesHistory] = useState(false);
  const [isOrderDialogOpen, setIsOrderDialogOpen] = useState(false);
  const [isSubmittingOrder, setIsSubmittingOrder] = useState(false);
  const [inventoryItems, setInventoryItems] = useState<any[]>([]);
  const [isLoadingInventory, setIsLoadingInventory] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<string>('');
  const [orderQuantity, setOrderQuantity] = useState<string>('1');
  const [catalogItems, setCatalogItems] = useState<any[]>([]);
  const [isAddInventoryDialogOpen, setIsAddInventoryDialogOpen] = useState(false);
  const [selectedCatalogItemId, setSelectedCatalogItemId] = useState<string>('');
  const [isDeletingInventory, setIsDeletingInventory] = useState<string | null>(null);

  const isOwner = currentRole === 'owner';
  const canEdit = isOwner || currentRole === 'coordinator';

  // Owner always; coordinator only if global switch or per-location flag is on
  const canViewFinancials = isOwner ||
    (currentRole === 'coordinator' && (globalCoordinatorViewFinancials || location?.coordinator_can_view_financials === true));

  /** Sections that have at least one task in “Widok bieżący” — used for windowed rendering when the list is long. */
  const sectionsWithCurrentViewTasks = useMemo(
    () =>
      sections.filter((section) =>
        currentViewTasks.some((task) => task.section_id === section.id)
      ),
    [sections, currentViewTasks]
  );

  useEffect(() => {
    if (!id) {
      toast.error(t('admin.property.toasts.missingId'));
      navigate('/dashboard');
      return;
    }

    fetchPropertyData();
  }, [id, currentOrgId]);

  useEffect(() => {
    if (location?.id) {
      fetchSections();
      fetchChecklists();
      fetchPersonnel();
      fetchScheduleChecklists();
      fetchCurrentViewTasks();
      fetchInventory();
      fetchMaterialRequests();
      setHistoryPage(0);
      setHistoryHasMore(true);
      fetchCompletedTasks(0, false);
      fetchIssuesHistory();
      fetchCatalog();
      fetchHolidays();
      
      // Initialize financial data
      if (location) {
        setEditedFinancialData({
          monthly_salary: location.monthly_salary || 0,
          cleaning_rate: location.cleaning_rate || 0,
          snow_removal_rate: location.snow_removal_rate || 0,
          extra_work_rate: location.extra_work_rate || 0,
          coordinator_can_view_financials: location.coordinator_can_view_financials || false,
        });
      }
    }
  }, [location?.id, currentOrgId, showArchivedSections]);

  useEffect(() => {
    setHistoryPage(0);
    setHistoryHasMore(true);
    fetchCompletedTasks(0, false);
  }, [historyDateRange, historyStaffFilter]);

  useEffect(() => {
    fetchIssuesHistory();
  }, [id, currentOrgId, issuesHistoryDateRange]);

  // Pobierz wykonawcę (actual_performer) gdy modal szczegółów jest otwarty i zadanie zakończone
  useEffect(() => {
    if (!selectedTaskDetails || !isTaskDetailsDialogOpen) {
      setTaskDetailsPerformerName(null);
      return;
    }
    const status = getEffectiveTaskStatus(selectedTaskDetails);
    if (status !== 'done') {
      setTaskDetailsPerformerName(null);
      return;
    }
    const performerId = (selectedTaskDetails as { actual_performer_id?: string | null }).actual_performer_id ?? selectedTaskDetails.assigned_staff_id;
    if (!performerId) {
      setTaskDetailsPerformerName(selectedTaskDetails.assignedStaffName ?? null);
      return;
    }
    if (selectedTaskDetails.assigned_staff_id === performerId && selectedTaskDetails.assignedStaffName) {
      setTaskDetailsPerformerName(selectedTaskDetails.assignedStaffName);
      return;
    }
    supabase.from('profiles').select('full_name').eq('id', performerId).maybeSingle().then(({ data }) => {
      setTaskDetailsPerformerName(data?.full_name ?? null);
    });
  }, [selectedTaskDetails, isTaskDetailsDialogOpen]);

  const fetchPropertyData = async () => {
    if (!id || !currentOrgId) return;

    setIsLoading(true);
    setIsLoadError(false);
    try {
      const [locationResult, orgResult] = await executeWithRetry(
        () =>
          Promise.all([
          supabase
            .from('cleaning_locations')
            .select('*, cleaning_clients (*)')
            .eq('id', id)
            .eq('org_id', currentOrgId)
            .eq('is_cleaning_active', true)
            .single(),
          supabase
            .from('organizations')
            .select('global_coordinator_view_financials')
            .eq('id', currentOrgId)
            .single(),
          ]),
        { isReadOperation: true }
      );

      const { data: locationData, error: locationError } = locationResult;

      if (locationError) {
        console.error('Error fetching location:', locationError);
        toast.error(t('admin.property.toasts.notFoundCleaning'));
        navigate('/dashboard');
        return;
      }

      if (!locationData) {
        toast.error(t('admin.property.toasts.notFoundCleaning'));
        navigate('/dashboard');
        return;
      }

      const locationWithClient = locationData as LocationWithClient;
      setLocation(locationWithClient);

      const orgData = orgResult.data as { global_coordinator_view_financials?: boolean | null } | null;
      setGlobalCoordinatorViewFinancials(orgData?.global_coordinator_view_financials ?? false);

      // Parse validation config
      if (locationWithClient.validation_config) {
        const config = locationWithClient.validation_config as ValidationConfig;
        setValidationConfig({
          qr_required: config.qr_required ?? false,
          gps_required: config.gps_required ?? false,
          biometric_required: config.biometric_required ?? false,
        });
      }

      // Initialize edit states with current data ONLY if not in edit mode
      // cleaning_clients from JOIN can be an object or array - handle both cases
      if (!isEditingClient && !isEditingInstructions) {
        let client: CleaningClient | null = null;
        if (locationWithClient.cleaning_clients) {
          if (Array.isArray(locationWithClient.cleaning_clients)) {
            client = locationWithClient.cleaning_clients[0] || null;
          } else {
            client = locationWithClient.cleaning_clients as CleaningClient;
          }
        }
        
        const contractDetails = (client?.contract_details as ContractDetails) || {};
        
        setEditedClientData({
          name: client?.name || '',
          email: client?.email || '',
          phone: client?.phone || '',
          nip: contractDetails.nip || '',
          address: contractDetails.address || '',
        });

        setEditedInstructions({
          access_code: locationWithClient.access_code || '',
          instruction_notes: locationWithClient.instruction_notes || '',
          coordinator_notes: locationWithClient.coordinator_notes || '',
        });
      }
    } catch (error: any) {
      console.error('Unexpected error:', error);
      toast.error(error?.message ?? t('admin.property.toasts.unexpected'));
      setIsLoadError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleValidationToggle = async (field: keyof ValidationConfig, value: boolean) => {
    if (!canEdit || !location) return;

    const newConfig = {
      ...validationConfig,
      [field]: value,
    };

    setValidationConfig(newConfig);

    try {
      setIsSaving(true);

      const { error } = await supabase
        .from('cleaning_locations')
        .update({ validation_config: newConfig })
        .eq('id', location.id);

      if (error) {
        console.error('Error updating validation config:', error);
        toast.error(t('admin.property.toasts.saveError'));
        // Revert on error
        setValidationConfig(validationConfig);
      } else {
        toast.success(t('admin.property.toasts.saved'));
      }
    } catch (error) {
      console.error('Unexpected error:', error);
      toast.error(t('admin.property.toasts.unexpected'));
      // Revert on error
      setValidationConfig(validationConfig);
    } finally {
      setIsSaving(false);
    }
  };

  const handleRegenerateQRToken = async () => {
    if (!canEdit || !location) return;

    try {
      setIsRegeneratingQR(true);

      // Generate new UUID token
      const newToken = crypto.randomUUID();

      const { error } = await supabase
        .from('cleaning_locations')
        .update({ qr_code_token: newToken })
        .eq('id', location.id);

      if (error) {
        console.error('Error regenerating QR token:', error);
        toast.error(t('admin.property.qr.regenerateError'));
      } else {
        toast.success(t('admin.property.qr.regenerated'));
        // Refresh location data
        await fetchPropertyData();
      }
    } catch (error) {
      console.error('Unexpected error:', error);
      toast.error(t('admin.property.toasts.unexpected'));
    } finally {
      setIsRegeneratingQR(false);
    }
  };

  const handlePrintQR = () => {
    if (!location?.qr_code_token) {
      toast.error(t('admin.property.qr.noQrToPrint'));
      return;
    }

    // Open print window
    window.print();
  };

  const handleEditClient = () => {
    // Copy current data to edit state
    let client: CleaningClient | null = null;
    if (location?.cleaning_clients) {
      if (Array.isArray(location.cleaning_clients)) {
        client = location.cleaning_clients[0] || null;
      } else {
        client = location.cleaning_clients as CleaningClient;
      }
    }
    
    const contractDetails = (client?.contract_details as ContractDetails) || {};
    
    setEditedClientData({
      name: client?.name || '',
      email: client?.email || '',
      phone: client?.phone || '',
      nip: contractDetails.nip || '',
      address: contractDetails.address || '',
    });
    
    setIsEditingClient(true);
  };

  const handleCancelEditClient = () => {
    // Reset to original values
    let client: CleaningClient | null = null;
    if (location?.cleaning_clients) {
      if (Array.isArray(location.cleaning_clients)) {
        client = location.cleaning_clients[0] || null;
      } else {
        client = location.cleaning_clients as CleaningClient;
      }
    }
    const contractDetails = (client?.contract_details as ContractDetails) || {};
    
    setEditedClientData({
      name: client?.name || '',
      email: client?.email || '',
      phone: client?.phone || '',
      nip: contractDetails.nip || '',
      address: contractDetails.address || '',
    });
    setIsEditingClient(false);
  };

  const handleSaveClient = async () => {
    if (!location) {
      toast.error(t('admin.property.toasts.missingProperty'));
      return;
    }

    if (!editedClientData.name.trim()) {
      toast.error(t('admin.property.client.nameRequired'));
      return;
    }

    try {
      setIsSaving(true);

      // Extract client if exists
      let client: CleaningClient | null = null;
      if (location.cleaning_clients) {
        if (Array.isArray(location.cleaning_clients)) {
          client = location.cleaning_clients[0] || null;
        } else {
          client = location.cleaning_clients as CleaningClient;
        }
      }

      // Update contract_details
      const contractDetails: ContractDetails = {
        nip: editedClientData.nip || undefined,
        address: editedClientData.address || undefined,
      };

      let error;
      if (client) {
        // Update existing client
        const { error: updateError } = await supabase
          .from('cleaning_clients')
          .update({
            name: editedClientData.name,
            email: editedClientData.email || null,
            phone: editedClientData.phone || null,
            contract_details: asJson(contractDetails),
          })
          .eq('id', client.id);
        error = updateError;
      } else {
        // Create new client and link to location
        const { data: newClient, error: insertError } = await supabase
          .from('cleaning_clients')
          .insert({
            name: editedClientData.name,
            email: editedClientData.email || null,
            phone: editedClientData.phone || null,
            contract_details: asJson(contractDetails),
            org_id: currentOrgId || '',
          })
          .select()
          .single();
        
        if (insertError || !newClient) {
          error = insertError;
        } else {
          // Link client to location
          const { error: linkError } = await supabase
            .from('cleaning_locations')
            .update({ client_id: newClient.id })
            .eq('id', location.id);
          error = linkError;
        }
      }

      if (error) {
        console.error('Error updating client:', error);
        toast.error(t('admin.property.toasts.saveError'));
        return;
      }

      // Update local state immediately
      const updatedClient: CleaningClient = client ? {
        ...client,
        name: editedClientData.name,
        email: editedClientData.email || null,
        phone: editedClientData.phone || null,
        contract_details: asJson(contractDetails),
      } : {
        id: '',
        name: editedClientData.name,
        email: editedClientData.email || null,
        phone: editedClientData.phone || null,
        contract_details: asJson(contractDetails),
        org_id: currentOrgId || '',
        created_at: null,
        updated_at: null,
      };

      if (client) {
        // Update existing client in local state
        setLocation({
          ...location,
          cleaning_clients: updatedClient,
        });
      } else {
        // New client was created - refresh to get full data with ID
        await fetchPropertyData();
      }

      toast.success(t('admin.property.client.saved'));
      setIsEditingClient(false);
    } catch (error) {
      console.error('Unexpected error:', error);
      toast.error(t('admin.property.toasts.unexpected'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditInstructions = () => {
    setIsEditingInstructions(true);
  };

  const handleCancelEditInstructions = () => {
    // Reset to original values
    setEditedInstructions({
      access_code: location?.access_code || '',
      instruction_notes: location?.instruction_notes || '',
      coordinator_notes: location?.coordinator_notes || '',
    });
    setIsEditingInstructions(false);
  };

  const handleSaveInstructions = async () => {
    if (!location) {
      toast.error(t('admin.property.toasts.missingProperty'));
      return;
    }

    try {
      setIsSaving(true);

      const { error } = await supabase
        .from('cleaning_locations')
        .update({
          access_code: editedInstructions.access_code || null,
          instruction_notes: editedInstructions.instruction_notes || null,
          coordinator_notes: editedInstructions.coordinator_notes || null,
        })
        .eq('id', location.id);

      if (error) {
        console.error('Error updating instructions:', error);
        toast.error(t('admin.property.toasts.saveError'));
        return;
      }

      // Update local state immediately
      setLocation({
        ...location,
        access_code: editedInstructions.access_code || null,
        instruction_notes: editedInstructions.instruction_notes || null,
        coordinator_notes: editedInstructions.coordinator_notes || null,
      });

      toast.success(t('admin.property.instructions.saved'));
      setIsEditingInstructions(false);
    } catch (error) {
      console.error('Unexpected error:', error);
      toast.error(t('admin.property.toasts.unexpected'));
    } finally {
      setIsSaving(false);
    }
  };


  const fetchSections = async () => {
    if (!id || !currentOrgId) return;

    try {
      setIsLoadingSections(true);

      let sectionsQuery = supabase
        .from('property_sections')
        .select(`
          id,
          location_id,
          name,
          is_active,
          sort_order,
          allow_cross_access,
          assigned_staff_id,
          created_at
        `)
        .eq('location_id', id);

      if (!showArchivedSections) {
        sectionsQuery = sectionsQuery.eq('is_active', true);
      }

      const { data: legacyData, error } = await sectionsQuery
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true });

      const data = legacyData || [];

      if (error) {
        console.error('Error fetching sections:', error);
        toast.error(t('admin.property.sections.fetchError'));
        return;
      }

      // Ensure all sections have sort_order
      const sectionsWithOrder = (data || [])
        .filter((section) => !isAssignmentPlaceholderSection(section.name))
        .map((section, index) => ({
        ...section,
        sort_order: section.sort_order ?? index,
      }));

      setSections(sectionsWithOrder);
    } catch (error) {
      console.error('Unexpected error:', error);
      toast.error(t('admin.property.toasts.unexpected'));
    } finally {
      setIsLoadingSections(false);
    }
  };

  const fetchChecklists = async () => {
    if (!location?.id || !currentOrgId) return;

    try {
      const { data, error } = await supabase
        .from('property_checklists')
        .select('id, org_id, location_id, section_id, name, frequency, frequency_config, is_active, baseline_date, requires_photo, created_at')
        .eq('location_id', location.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching checklists:', error);
        toast.error(t('admin.property.toasts.fetchTasksError'));
        return;
      }

      setChecklists(data || []);
    } catch (error) {
      console.error('Unexpected error:', error);
      toast.error(t('admin.property.toasts.unexpected'));
    }
  };

  const fetchPersonnel = async () => {
    if (!location?.id || !currentOrgId) return;

    try {
      setIsLoadingPersonnel(true);
      
      const { data: accessRows, error: accessError } = await supabase
        .from('location_access')
        .select('user_id')
        .eq('location_id', location.id)
        .is('unit_number', null);

      if (accessError) {
        console.error('Error fetching location_access:', accessError);
        toast.error(t('admin.property.personnel.fetchError'));
        return;
      }

      const userIds = [...new Set((accessRows || []).map((row) => row.user_id).filter(Boolean))];

      if (userIds.length === 0) {
        setPersonnel([]);
        return;
      }

      // Fetch profiles for these user IDs with join
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, full_name')
        .in('id', userIds);

      if (profilesError) {
        console.error('Error fetching profiles:', profilesError);
        toast.error(t('admin.property.personnel.fetchProfilesError'));
        return;
      }

      // Get memberships to determine roles
      const { data: memberships, error: membershipsError } = await supabase
        .from('memberships')
        .select('user_id, role')
        .eq('org_id', currentOrgId)
        .in('user_id', userIds);

      const roleMap = new Map<string, string>();
      if (memberships && !membershipsError) {
        memberships.forEach(m => {
          roleMap.set(m.user_id, m.role);
        });
      }

      // Add status and role to each personnel
      const personnelWithStatus = (profiles || []).map(profile => ({
        ...profile,
        status: 'Aktywny' as string, // Statyczny badge - później podepniemy pod realny status
        role: roleMap.get(profile.id) || 'cleaner',
      }));

      setPersonnel(personnelWithStatus);
    } catch (error) {
      console.error('Unexpected error:', error);
      toast.error(t('admin.property.toasts.unexpected'));
    } finally {
      setIsLoadingPersonnel(false);
    }
  };

  const handleAddSection = async () => {
    if (!canEdit) {
      toast.error(t('admin.property.sections.noPermissionAdd'));
      return;
    }
    if (!location?.id || !newSectionName.trim()) {
      toast.error(t('admin.property.sections.nameRequired'));
      return;
    }

    try {
      setIsSaving(true);
      
      // Get max sort_order for this location
      const { data: existingSections } = await supabase
        .from('property_sections')
        .select('sort_order')
        .eq('location_id', location.id)
        .order('sort_order', { ascending: false })
        .limit(1);

      const maxSortOrder = existingSections && existingSections.length > 0 
        ? (existingSections[0].sort_order ?? 0) + 1 
        : 0;

      const { error } = await supabase
        .from('property_sections')
        .insert({
          location_id: location.id,
          name: newSectionName.trim(),
          sort_order: maxSortOrder,
        });

      if (error) {
        console.error('Error adding section:', error);
        toast.error(t('admin.property.sections.addError'));
        return;
      }

      toast.success(t('admin.property.sections.added'));
      setIsAddSectionDialogOpen(false);
      setNewSectionName('');
      await fetchSections();
    } catch (error) {
      console.error('Unexpected error:', error);
      toast.error(t('admin.property.toasts.unexpected'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleMoveSection = async (sectionId: string, direction: 'up' | 'down') => {
    if (!canEdit || !location?.id) return;

    const currentIndex = sections.findIndex(s => s.id === sectionId);
    if (currentIndex === -1) return;

    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (targetIndex < 0 || targetIndex >= sections.length) return;

    const currentSection = sections[currentIndex];
    const targetSection = sections[targetIndex];

    try {
      setIsSaving(true);

      // Fetch current sort_order values from database to ensure accuracy
      const { data: currentData, error: currentError } = await supabase
        .from('property_sections')
        .select('sort_order')
        .eq('id', currentSection.id)
        .single();

      const { data: targetData, error: targetError } = await supabase
        .from('property_sections')
        .select('sort_order')
        .eq('id', targetSection.id)
        .single();

      if (currentError || targetError) {
        console.error('Error fetching sort_order:', currentError || targetError);
        toast.error(t('admin.property.sections.fetchDataError'));
        return;
      }

      // Use database values or fallback to index
      const currentSortOrder = currentData?.sort_order ?? currentIndex;
      const targetSortOrder = targetData?.sort_order ?? targetIndex;

      // Update both sections in parallel
      const [updateCurrent, updateTarget] = await Promise.all([
        supabase
          .from('property_sections')
          .update({ sort_order: targetSortOrder })
          .eq('id', currentSection.id),
        supabase
          .from('property_sections')
          .update({ sort_order: currentSortOrder })
          .eq('id', targetSection.id),
      ]);

      if (updateCurrent.error || updateTarget.error) {
        console.error('Error moving section:', updateCurrent.error || updateTarget.error);
        toast.error(t('admin.property.sections.orderError'));
        return;
      }

      toast.success(t('admin.property.sections.orderChanged'));
      // Force refresh sections from database
      await fetchSections();
    } catch (error) {
      console.error('Unexpected error:', error);
      toast.error(t('admin.property.toasts.unexpected'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateSection = async () => {
    if (!canEdit) {
      toast.error(t('admin.property.sections.noPermissionEdit'));
      return;
    }
    if (!editingSection || !newSectionName.trim()) {
      toast.error(t('admin.property.sections.nameRequired'));
      return;
    }

    try {
      setIsSaving(true);
      const { error } = await supabase
        .from('property_sections')
        .update({ name: newSectionName.trim() })
        .eq('id', editingSection.id);

      if (error) {
        console.error('Error updating section:', error);
        toast.error(t('admin.property.sections.updateError'));
        return;
      }

      toast.success(t('admin.property.sections.updated'));
      setIsEditSectionDialogOpen(false);
      setEditingSection(null);
      setNewSectionName('');
      await fetchSections();
    } catch (error) {
      console.error('Unexpected error:', error);
      toast.error(t('admin.property.toasts.unexpected'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteSection = async (section: PropertySection) => {
    if (!canEdit) {
      toast.error(t('admin.property.sections.noPermissionArchive'));
      return;
    }

    // Window.confirm before archiving
    if (!window.confirm(t('admin.property.sections.confirmArchive', { name: section.name }))) {
      return;
    }

    try {
      setIsSaving(true);
      const { error } = await supabase
        .from('property_sections')
        .update({ is_active: false })
        .eq('id', section.id);

      if (error) {
        console.error('Error archiving section:', error);
        toast.error(t('admin.property.sections.archiveError'));
        return;
      }

      toast.success(t('admin.property.sections.archivedToast'));
      await fetchSections();
      await fetchChecklists();
    } catch (error) {
      console.error('Unexpected error:', error);
      toast.error(t('admin.property.toasts.unexpected'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleRestoreSection = async (section: PropertySection) => {
    if (!canEdit) {
      toast.error(t('admin.property.sections.noPermissionRestore'));
      return;
    }

    try {
      setIsSaving(true);
      const { error } = await supabase
        .from('property_sections')
        .update({ is_active: true })
        .eq('id', section.id);

      if (error) {
        console.error('Error restoring section:', error);
        toast.error(t('admin.property.sections.restoreError'));
        return;
      }

      toast.success(t('admin.property.sections.restoredToast'));
      await fetchSections();
      await fetchChecklists();
    } catch (error) {
      console.error('Unexpected error:', error);
      toast.error(t('admin.property.toasts.unexpected'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddTask = async () => {
    if (!canEdit) {
      toast.error(t('admin.property.tasks.noPermissionAdd'));
      return;
    }
    if (!currentOrgId) {
      toast.error(t('admin.property.toasts.noOrg'));
      return;
    }
    if (!selectedSectionForTask || !newTaskName.trim()) {
      toast.error(t('admin.property.tasks.nameRequired'));
      return;
    }

    // Validate specific_days
    if (newTaskFrequencyType === 'specific_days' && selectedDays.length === 0) {
      toast.error(t('admin.property.tasks.selectAtLeastOneDay'));
      return;
    }

    try {
      setIsSaving(true);

      // Generate frequency text and config
      const daysArray = selectedDays.map(d => parseInt(d));
      const frequencyText = generateFrequencyText(
        newTaskFrequencyType,
        daysArray.length > 0 ? daysArray : undefined
      );
      const frequencyConfig = generateFrequencyConfig(
        newTaskFrequencyType,
        daysArray.length > 0 ? daysArray : undefined
      );

      const { error } = await supabase
        .from('property_checklists')
        .insert({
          location_id: location?.id,
          section_id: selectedSectionForTask,
          name: newTaskName.trim(),
          frequency: frequencyText,
          frequency_config: asJson(frequencyConfig),
          requires_photo: newTaskRequiresPhoto,
          org_id: currentOrgId,
        });

      if (error) {
        console.error('Error adding task:', error);
        toast.error(t('admin.property.tasks.addError'));
        return;
      }

      toast.success(t('admin.property.tasks.added'));
      setIsAddTaskDialogOpen(false);
      setSelectedSectionForTask('');
      setNewTaskName('');
      setNewTaskFrequencyType('daily');
      setSelectedDays([]);
      setNewTaskRequiresPhoto(false);
      await fetchChecklists();
      await fetchScheduleChecklists();
    } catch (error) {
      console.error('Unexpected error:', error);
      toast.error(t('admin.property.toasts.unexpected'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteChecklist = async (taskId: string) => {
    if (!canEdit) return;

    if (!confirm(t('admin.property.tasks.confirmDelete'))) {
      return;
    }

    try {
      setIsSaving(true);
      const { error } = await supabase
        .from('property_checklists')
        .delete()
        .eq('id', taskId);

      if (error) {
        console.error('Error deleting task:', error);
        toast.error(t('admin.property.tasks.deleteError'));
        return;
      }

      toast.success(t('admin.property.tasks.deleted'));
      await fetchChecklists();
      await fetchScheduleChecklists();
    } catch (error) {
      console.error('Unexpected error:', error);
      toast.error(t('admin.property.toasts.unexpected'));
    } finally {
      setIsSaving(false);
    }
  };

  const openEditSectionDialog = (section: PropertySection) => {
    setEditingSection(section);
    setNewSectionName(section.name);
    setIsEditSectionDialogOpen(true);
  };

  const openDeleteSectionDialog = (section: PropertySection) => {
    handleDeleteSection(section);
  };

  const openAddTaskDialog = (sectionId: string) => {
    setSelectedSectionForTask(sectionId);
    setNewTaskFrequencyType('daily');
    setSelectedDays([]);
    setIsAddTaskDialogOpen(true);
  };

  // Helper functions for frequency formatting
  const getFrequencyLabel = (type: string): string => {
    const labels: Record<string, string> = {
      daily: t('admin.property.frequency.daily'),
      work_days: t('admin.property.frequency.work_days'),
      specific_days: t('admin.property.frequency.specific_days'),
      weekly: t('admin.property.frequency.weekly'),
      biweekly: t('admin.property.frequency.biweekly'),
      monthly: t('admin.property.frequency.monthly'),
      quarterly: t('admin.property.frequency.quarterly'),
      custom: t('admin.property.frequency.custom'),
    };
    return labels[type] || type;
  };

  const getDayLabel = (dayIndex: number): string => {
    const days = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
    const key = days[dayIndex];
    return key ? t(`admin.property.days.short.${key}`) : '';
  };

  const getDayFullName = (dayIndex: number): string => {
    const days = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
    const key = days[dayIndex];
    return key ? t(`admin.property.days.full.${key}`) : '';
  };

  const generateFrequencyText = (
    type: string,
    days?: number[],
    customText?: string
  ): string => {
    switch (type) {
      case 'daily':
        return 'Codziennie';
      case 'work_days':
        return 'Codziennie w dni robocze (Pn-Pt)';
      case 'specific_days':
        if (days && days.length > 0) {
          const polishDays = ['Pon', 'Wt', 'Śr', 'Czw', 'Pt', 'Sob', 'Nie'];
          return days.map((day) => polishDays[day] || '').join(', ');
        }
        return 'W określone dni tygodnia';
      case 'weekly':
        return 'Raz na tydzień';
      case 'biweekly':
        return 'Raz na dwa tygodnie';
      case 'monthly':
        return 'Raz na miesiąc';
      case 'quarterly':
        return 'Raz na kwartał';
      case 'custom':
        return customText || 'Inne';
      default:
        return 'Codziennie';
    }
  };

  const generateFrequencyConfig = (
    type: string,
    days?: number[],
    customText?: string
  ) => {
    const config: {
      type: string;
      days?: number[];
      text?: string;
    } = { type };

    if (type === 'specific_days' && days && days.length > 0) {
      config.days = days;
    } else if (type === 'custom' && customText) {
      config.text = customText;
    }

    return config;
  };

  const formatFrequencyDisplay = (task: PropertyChecklist): string => {
    const frequency = parseFrequencyConfig(task.frequency_config);
    if (!frequency) {
      return task.frequency || t('admin.property.frequency.daily');
    }
    if (frequency.type === 'specific_days') {
      if (frequency.days && frequency.days.length > 0) {
        return frequency.days.map((day) => getDayFullName(day)).join(', ');
      }
      return t('admin.property.frequency.specific_days');
    }
    if (frequency.type === 'custom') {
      return frequency.text || t('admin.property.frequency.other');
    }
    switch (frequency.type) {
      case 'daily':
        return t('admin.property.frequency.daily');
      case 'work_days':
        return t('admin.property.frequency.work_days');
      case 'weekly':
        return t('admin.property.frequency.weekly');
      case 'biweekly':
        return t('admin.property.frequency.biweekly');
      case 'monthly':
        return t('admin.property.frequency.monthly');
      case 'quarterly':
        return t('admin.property.frequency.quarterly');
      default:
        return t('admin.property.frequency.daily');
    }
  };

  /** Frequency type order for schedule sort: Jednorazowe < Codziennie < Tygodniowo < ... */
  const getScheduleFrequencyOrder = (task: PropertyChecklist): number => {
    const type = parseFrequencyConfig(task.frequency_config)?.type ?? 'daily';
    const order: Record<string, number> = {
      daily: 1,
      work_days: 2,
      specific_days: 3,
      weekly: 4,
      biweekly: 5,
      monthly: 6,
      quarterly: 7,
      custom: 8,
    };
    return order[type] ?? 99;
  };

  const handleScheduleSort = (key: string) => {
    setScheduleSortConfig((prev) => {
      const direction = prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc';
      return { key, direction };
    });
  };

  const getScheduleSectionName = (task: PropertyChecklist): string => {
    return task.section_id
      ? (task.property_sections as { name?: string } | null)?.name ?? t('admin.property.unknownSection')
      : t('admin.property.wholeObject');
  };

  const fetchAvailablePersonnel = async () => {
    if (!currentOrgId || !location?.id) return;

    try {
      setIsLoadingAvailablePersonnel(true);

      // Get all users in the organization
      const { data: memberships, error: membershipsError } = await supabase
        .from('memberships')
        .select('user_id')
        .eq('org_id', currentOrgId)
        .in('role', [...CLEANING_STAFF_ROLES])
        .eq('is_active', true);

      if (membershipsError) {
        console.error('Error fetching memberships:', membershipsError);
        toast.error(t('admin.property.personnel.fetchListError'));
        return;
      }

      const allUserIds = (memberships || []).map(m => m.user_id);

      if (allUserIds.length === 0) {
        setAvailablePersonnel([]);
        return;
      }

      const { data: accessRows } = await supabase
        .from('location_access')
        .select('user_id')
        .eq('location_id', location.id)
        .is('unit_number', null);

      const assignedUserIds = new Set((accessRows || []).map((row) => row.user_id).filter(Boolean));

      // Filter out already assigned users
      const availableUserIds = allUserIds.filter(id => !assignedUserIds.has(id));

      if (availableUserIds.length === 0) {
        setAvailablePersonnel([]);
        return;
      }

      // Fetch profiles for available users
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, full_name')
        .in('id', availableUserIds);

      if (profilesError) {
        console.error('Error fetching profiles:', profilesError);
        toast.error(t('admin.property.personnel.fetchProfilesError'));
        return;
      }

      setAvailablePersonnel(profiles || []);
    } catch (error) {
      console.error('Unexpected error:', error);
      toast.error(t('admin.property.toasts.unexpected'));
    } finally {
      setIsLoadingAvailablePersonnel(false);
    }
  };

  const handleAssignPersonnel = async () => {
    if (!canEdit || !location?.id || !selectedPersonnelId) {
      toast.error(t('admin.property.personnel.selectWorker'));
      return;
    }

    try {
      setIsSaving(true);

      const { alreadyAssigned } = await grantStaffLocationAccess(location.id, selectedPersonnelId);
      if (alreadyAssigned) {
        toast.error(t('admin.property.personnel.alreadyAssigned'));
        return;
      }

      toast.success(t('admin.property.personnel.assigned'));
      setIsAssignPersonnelDialogOpen(false);
      setSelectedPersonnelId('');
      await fetchPersonnel();
    } catch (error: any) {
      console.error('Unexpected error:', error);
      toast.error(error?.message || t('admin.property.toasts.unexpected'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemovePersonnelAccess = async (userId: string) => {
    if (!canEdit || !location?.id) return;

    try {
      setIsSaving(true);

      await revokeStaffLocationAccess(location.id, userId);

      toast.success(t('admin.property.personnel.accessRemoved'));
      setPersonnelToDelete(null);
      await fetchPersonnel();
    } catch (error: any) {
      console.error('Unexpected error:', error);
      toast.error(error?.message || t('admin.property.toasts.unexpected'));
    } finally {
      setIsSaving(false);
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'W pracy':
        return 'default';
      case 'Wolny':
        return 'default';
      case 'Urlop':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'W pracy':
        return 'bg-blue-500 text-white hover:bg-blue-600';
      case 'Wolny':
        return 'bg-green-500 text-white hover:bg-green-600';
      case 'Urlop':
        return 'bg-yellow-500 text-white hover:bg-yellow-600';
      default:
        return '';
    }
  };

  // Schedule/Harmonogram functions
  const fetchScheduleChecklists = async () => {
    if (!location?.id || !currentOrgId) return;

    try {
      setIsLoadingSchedule(true);
      const { data, error } = await supabase
        .from('property_checklists')
        .select(`
          *,
          property_sections:section_id (
            name
          )
        `)
        .eq('location_id', location.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching schedule checklists:', error);
        toast.error(t('admin.property.schedule.fetchError'));
        return;
      }

      // Map sections - Supabase returns array or object depending on relationship
      const mappedData = (data || []).map((task) => ({
        ...task,
        property_sections: firstJoined(task.property_sections),
      }));

      setScheduleChecklists(mappedData);
    } catch (error) {
      console.error('Unexpected error:', error);
      toast.error(t('admin.property.toasts.unexpected'));
    } finally {
      setIsLoadingSchedule(false);
    }
  };

  // Current View Tasks functions
  const fetchCurrentViewTasks = async () => {
    if (!location?.id || !currentOrgId) return;

    try {
      setIsLoadingCurrentViewTasks(true);
      
      // Get today's date at 00:00:00
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayISO = today.toISOString();

      // Get today's end date at 23:59:59
      const todayEnd = new Date();
      todayEnd.setHours(23, 59, 59, 999);
      const todayEndISO = todayEnd.toISOString();

      // FIX: Pobierz WSZYSTKIE zadania dla nieruchomości na dziś (nie filtruj po status != 'done')
      // - sop_standard: scheduled_at = today (00:00:00 - 23:59:59) - wszystkie statusy
      // - coordinator_single: wszystkie zadania (niezależnie od statusu)
      // - employee_extra: wszystkie zadania (niezależnie od statusu)
      // - long_term: scheduled_at <= today - wszystkie statusy
      // Ukończone zadania będą widoczne, ale wyszarzone/przekreślone
      const { data: tasksData, error: tasksError } = await supabase
        .from('cleaning_tasks')
        .select(`
          *,
          property_sections:section_id (
            name
          ),
          profiles!cleaning_tasks_assigned_staff_id_fkey(full_name)
        `)
        .eq('location_id', location.id)
        .eq('org_id', currentOrgId)
        .in('task_type', ['sop_standard', 'coordinator_single', 'employee_extra', 'long_term'])
        .or(`and(task_type.eq.sop_standard,scheduled_at.gte.${todayISO},scheduled_at.lte.${todayEndISO}),and(task_type.eq.coordinator_single),and(task_type.eq.employee_extra),and(task_type.eq.long_term,scheduled_at.lte.${todayEndISO})`)

      if (tasksError) {
        console.error('Error fetching current view tasks:', tasksError);
        toast.error(t('admin.property.toasts.fetchTasksError'));
        return;
      }

      const allTasks = tasksData || [];

      // Map sections and profiles - Supabase returns array or object depending on relationship
      const mappedData = allTasks.map((task) => {
        let sectionData = null;
        if (task.property_sections) {
          if (Array.isArray(task.property_sections)) {
            sectionData = task.property_sections[0] || null;
          } else {
            sectionData = task.property_sections;
          }
        }
        const prof = task.profiles;
        const staffName = prof
          ? (Array.isArray(prof) ? prof[0]?.full_name ?? null : (prof as { full_name?: string | null }).full_name ?? null)
          : null;
        
        return {
          ...task,
          property_sections: sectionData,
          assignedStaffName: staffName,
        } as CleaningTask;
      });

      // Current view: pending/in_progress always; done only if completed today; ukryj cancelled (soft delete)
      const filteredData = mappedData.filter((t) => {
        const status = getEffectiveTaskStatus(t);
        if (status === 'cancelled') return false;
        return status !== 'done' || isTaskCompletedToday(t);
      });

      // Sort: coordinator_single, sop_standard, long_term, employee_extra; within group newest first (created_at desc)
      const TASK_TYPE_ORDER: Record<string, number> = {
        coordinator_single: 0,
        sop_standard: 1,
        long_term: 2,
        employee_extra: 3,
      };
      const sortedData = [...filteredData].sort((a, b) => {
        const orderA = TASK_TYPE_ORDER[a.task_type ?? ''] ?? 99;
        const orderB = TASK_TYPE_ORDER[b.task_type ?? ''] ?? 99;
        if (orderA !== orderB) return orderA - orderB;
        const timeA = a.created_at ? new Date(a.created_at).getTime() : 0;
        const timeB = b.created_at ? new Date(b.created_at).getTime() : 0;
        return timeB - timeA; // newest first
      });

      setCurrentViewTasks(sortedData);
    } catch (error) {
      console.error('Unexpected error:', error);
      toast.error(t('admin.property.toasts.unexpected'));
    } finally {
      setIsLoadingCurrentViewTasks(false);
    }
  };

  // Delete task function
  const handleDeleteTask = async () => {
    if (!taskToDelete || !currentOrgId) return;
    if (taskToDelete.is_paid) {
      toast.error(t('admin.property.tasks.paidCannotDelete'));
      setTaskToDelete(null);
      return;
    }

    const taskId = taskToDelete.id;

    try {
      setIsSaving(true);

      // KROK 1: Usuń powiązane logi (klucze obce) przed usunięciem zadania
      await supabase.from('task_step_logs').delete().eq('task_id', taskId);
      await supabase.from('task_execution_logs').delete().eq('task_id', taskId);

      // KROK 2: Hard delete zadania
      const { data: deletedRows, error } = await supabase
        .from('cleaning_tasks')
        .delete()
        .eq('id', taskId)
        .eq('org_id', currentOrgId)
        .select('id');

      if (error) {
        console.error('Błąd usuwania zadania:', error);
        // Fallback: Soft delete (status: cancelled) przy błędzie FK
        const isFkError = error.code === '23503' || error.message?.includes('foreign key') || error.message?.includes('violates foreign key');
        if (isFkError) {
          const { data: cancelledRows, error: updateError } = await supabase
            .from('cleaning_tasks')
            .update({ status: 'cancelled' })
            .eq('id', taskId)
            .eq('org_id', currentOrgId)
            .select('id');

          if (updateError || !cancelledRows?.length) {
            console.error('Błąd soft delete zadania:', updateError);
            toast.error(t('admin.property.tasks.deleteError'));
            return;
          }
          // Soft delete OK – zadanie zniknie z listy (filtrowane w fetchCurrentViewTasks)
        } else {
          toast.error(t('admin.property.tasks.deleteError'));
          return;
        }
      } else if (!deletedRows?.length) {
        toast.error(t('admin.property.tasks.deleteNotConfirmed'));
        return;
      }

      // Optimistic UI: natychmiast usuń z lokalnego stanu (hard delete lub soft delete)
      setCurrentViewTasks((prev) => prev.filter((t) => t.id !== taskId));
      toast.success(t('admin.property.tasks.deleted'));
      setTaskToDelete(null);
    } catch (error: unknown) {
      console.error('Błąd usuwania zadania:', error);
      toast.error(error instanceof Error ? error.message : t('admin.property.toasts.unexpected'));
    } finally {
      setIsSaving(false);
    }
  };

  // Financial data functions
  const handleEditFinancials = () => {
    if (!canViewFinancials || !location) return;
    setEditedFinancialData({
      monthly_salary: location.monthly_salary || 0,
      cleaning_rate: location.cleaning_rate || 0,
      snow_removal_rate: location.snow_removal_rate || 0,
      extra_work_rate: location.extra_work_rate || 0,
      coordinator_can_view_financials: location.coordinator_can_view_financials || false,
    });
    setIsEditingFinancials(true);
  };

  const handleCancelEditFinancials = () => {
    if (location) {
      setEditedFinancialData({
        monthly_salary: location.monthly_salary || 0,
        cleaning_rate: location.cleaning_rate || 0,
        snow_removal_rate: location.snow_removal_rate || 0,
        extra_work_rate: location.extra_work_rate || 0,
        coordinator_can_view_financials: location.coordinator_can_view_financials || false,
      });
    }
    setIsEditingFinancials(false);
  };

  const handleSaveFinancials = async () => {
    if (!location) {
      toast.error(t('admin.property.toasts.missingProperty'));
      return;
    }
    if (!canViewFinancials) {
      toast.error(t('admin.property.toasts.noPermission'));
      return;
    }

    try {
      setIsSaving(true);

      const payload: {
        monthly_salary: number | null;
        cleaning_rate: number | null;
        snow_removal_rate: number | null;
        extra_work_rate: number | null;
        coordinator_can_view_financials?: boolean;
      } = {
        monthly_salary: editedFinancialData.monthly_salary || null,
        cleaning_rate: editedFinancialData.cleaning_rate || null,
        snow_removal_rate: editedFinancialData.snow_removal_rate || null,
        extra_work_rate: editedFinancialData.extra_work_rate || null,
      };
      if (isOwner) {
        payload.coordinator_can_view_financials = editedFinancialData.coordinator_can_view_financials;
      }

      const { error } = await supabase
        .from('cleaning_locations')
        .update(payload)
        .eq('id', location.id);

      if (error) {
        console.error('Error updating financial data:', error);
        toast.error(t('admin.property.finance.saveError'));
        return;
      }

      // Update local state
      setLocation({
        ...location,
        monthly_salary: editedFinancialData.monthly_salary,
        cleaning_rate: editedFinancialData.cleaning_rate,
        snow_removal_rate: editedFinancialData.snow_removal_rate,
        extra_work_rate: editedFinancialData.extra_work_rate,
        coordinator_can_view_financials: isOwner
          ? editedFinancialData.coordinator_can_view_financials
          : location.coordinator_can_view_financials,
      });

      toast.success(t('admin.property.finance.saved'));
      setIsEditingFinancials(false);
    } catch (error) {
      console.error('Unexpected error:', error);
      toast.error(t('admin.property.toasts.unexpected'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleCoordinatorFinancials = async (checked: boolean) => {
    if (!location || !isOwner) return;

    try {
      setIsSaving(true);

      const { error } = await supabase
        .from('cleaning_locations')
        .update({ coordinator_can_view_financials: checked })
        .eq('id', location.id);

      if (error) {
        console.error('Error updating coordinator financials flag:', error);
        toast.error(t('admin.property.toasts.saveError'));
        return;
      }

      // Update local state
      setLocation({
        ...location,
        coordinator_can_view_financials: checked,
      });

      setEditedFinancialData({
        ...editedFinancialData,
        coordinator_can_view_financials: checked,
      });

      toast.success(t('admin.property.toasts.saved'));
    } catch (error) {
      console.error('Unexpected error:', error);
      toast.error(t('admin.property.toasts.unexpected'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleAutoNotifyIssues = async (checked: boolean) => {
    if (!location) return;

    try {
      setIsSaving(true);

      const { error } = await supabase
        .from('cleaning_locations')
        .update({ auto_notify_issues: checked })
        .eq('id', location.id);

      if (error) {
        console.error('Error updating auto_notify_issues:', error);
        toast.error(t('admin.property.toasts.saveError'));
        return;
      }

      // Update local state
      setLocation({
        ...location,
        auto_notify_issues: checked,
      });

      toast.success(t('admin.property.toasts.saved'));
    } catch (error) {
      console.error('Unexpected error:', error);
      toast.error(t('admin.property.toasts.unexpected'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleArchiveProperty = async () => {
    if (!id || !currentOrgId) return;
    
    if (!confirm(t('admin.property.propertyMgmt.confirmArchive'))) {
      return;
    }

    try {
      setIsSaving(true);
      const { error } = await supabase
        .from('cleaning_locations')
        .update({ status: 'archived' })
        .eq('id', id)
        .eq('org_id', currentOrgId);

      if (error) {
        console.error('Error archiving property:', error);
        toast.error(t('admin.property.propertyMgmt.archiveError'));
        return;
      }

      toast.success(t('admin.property.propertyMgmt.archivedToast'));
      navigate('/dashboard');
    } catch (error) {
      console.error('Unexpected error:', error);
      toast.error(t('admin.property.toasts.unexpected'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleRestoreProperty = async () => {
    if (!id || !currentOrgId) return;

    try {
      setIsSaving(true);
      const { error } = await supabase
        .from('cleaning_locations')
        .update({ status: 'active' })
        .eq('id', id)
        .eq('org_id', currentOrgId);

      if (error) {
        console.error('Error restoring property:', error);
        toast.error(t('admin.property.propertyMgmt.restoreError'));
        return;
      }

      toast.success(t('admin.property.propertyMgmt.restoredToast'));
      if (location) {
        setLocation({ ...location, status: 'active' });
      }
      await fetchPropertyData();
    } catch (error) {
      console.error('Unexpected error:', error);
      toast.error(t('admin.property.toasts.unexpected'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveNotificationEmail = async (email: string) => {
    if (!location) return;

    try {
      setIsSaving(true);

      const { error } = await supabase
        .from('cleaning_locations')
        .update({ client_notification_email: email || null })
        .eq('id', location.id);

      if (error) {
        console.error('Error updating client_notification_email:', error);
        toast.error(t('admin.property.toasts.saveError'));
        return;
      }

      // Update local state
      setLocation({
        ...location,
        client_notification_email: email || null,
      });

      toast.success(t('admin.property.toasts.saved'));
    } catch (error) {
      console.error('Unexpected error:', error);
      toast.error(t('admin.property.toasts.unexpected'));
    } finally {
      setIsSaving(false);
    }
  };

  const fetchInventory = async () => {
    if (!id || !currentOrgId) return;

    try {
      setIsLoadingInventory(true);
      const { data, error } = await supabase
        .from('cleaning_inventory')
        .select('*, cleaning_catalog(name, unit)')
        .eq('location_id', id)
        .eq('org_id', currentOrgId)
        .order('item_name', { ascending: true });

      if (error) {
        console.error('Error fetching inventory:', error);
        toast.error(t('admin.property.warehouse.fetchInventoryError'));
        return;
      }

      // Obsługa pustych danych po RLS: cleaning_inventory może zwrócić pustą tablicę jeśli użytkownik nie ma dostępu
      // Obsługa NULL w JOIN: cleaning_catalog może być null jeśli catalog_item_id jest null (już obsłużone w UI przez ?.)
      setInventoryItems(data || []);
    } catch (error) {
      console.error('Unexpected error:', error);
      toast.error(t('admin.property.toasts.unexpected'));
    } finally {
      setIsLoadingInventory(false);
    }
  };

  const fetchCatalog = async () => {
    if (!currentOrgId) return;

    try {
      const { data, error } = await supabase
        .from('cleaning_catalog')
        .select('id, name, unit')
        .eq('org_id', currentOrgId)
        .order('name', { ascending: true });

      if (error) {
        console.error('Error fetching catalog:', error);
        toast.error(t('admin.property.warehouse.fetchCatalogError'));
        return;
      }

      setCatalogItems(data || []);
    } catch (error) {
      console.error('Unexpected error:', error);
      toast.error(t('admin.property.toasts.unexpected'));
    }
  };

  const handleAddInventoryItem = async () => {
    if (!id || !currentOrgId || !selectedCatalogItemId) {
      toast.error(t('admin.property.warehouse.selectCatalogProduct'));
      return;
    }

    try {
      const catalogItem = catalogItems.find(item => item.id === selectedCatalogItemId);
      if (!catalogItem) {
        toast.error(t('admin.property.warehouse.productNotInCatalog'));
        return;
      }

      // Check if product is already in inventory
      const existingItem = inventoryItems.find(
        item => item.catalog_item_id === selectedCatalogItemId
      );
      if (existingItem) {
        toast.error(t('admin.property.warehouse.alreadyInAssortment'));
        return;
      }

      const { error } = await supabase
        .from('cleaning_inventory')
        .insert({
          org_id: currentOrgId,
          location_id: id,
          catalog_item_id: selectedCatalogItemId,
          item_name: catalogItem.name,
          unit: catalogItem.unit,
          current_quantity: 0,
        });

      if (error) {
        console.error('Error adding inventory item:', error);
        toast.error(t('admin.property.warehouse.addProductError', { message: error.message }));
        return;
      }

      toast.success(t('admin.property.warehouse.productAdded'));
      setSelectedCatalogItemId('');
      setIsAddInventoryDialogOpen(false);
      fetchInventory();
    } catch (error) {
      console.error('Unexpected error:', error);
      toast.error(t('admin.property.toasts.unexpected'));
    }
  };

  const handleDeleteInventoryItem = async (itemId: string) => {
    if (!currentOrgId) return;

    try {
      setIsDeletingInventory(itemId);
      const { error } = await supabase
        .from('cleaning_inventory')
        .delete()
        .eq('id', itemId)
        .eq('org_id', currentOrgId);

      if (error) {
        console.error('Error deleting inventory item:', error);
        toast.error(t('admin.property.warehouse.deleteProductError', { message: error.message }));
        return;
      }

      toast.success(t('admin.property.warehouse.productRemoved'));
      fetchInventory();
    } catch (error) {
      console.error('Unexpected error:', error);
      toast.error(t('admin.property.toasts.unexpected'));
    } finally {
      setIsDeletingInventory(null);
    }
  };


  const fetchMaterialRequests = async () => {
    if (!id || !currentOrgId) return;

    try {
      setIsLoadingMaterialRequests(true);
      const { data, error } = await supabase
        .from('material_requests')
        .select('*, profiles(full_name)')
        .eq('location_id', id)
        .eq('org_id', currentOrgId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching material requests:', error);
        toast.error(t('admin.property.warehouse.fetchOrdersError'));
        return;
      }

      // Obsługa pustych danych po RLS: material_requests może zwrócić pustą tablicę jeśli użytkownik nie ma dostępu
      // Obsługa NULL w JOIN: profiles może być null jeśli requester_id jest null lub użytkownik nie istnieje (już obsłużone w UI przez ?.)
      setMaterialRequests(data || []);
    } catch (error) {
      console.error('Unexpected error:', error);
      toast.error(t('admin.property.toasts.unexpected'));
    } finally {
      setIsLoadingMaterialRequests(false);
    }
  };

  const fetchCompletedTasks = async (page: number, append: boolean) => {
    if (!id || !currentOrgId) return;

    const from = page * HISTORY_ITEMS_PER_PAGE;
    const to = from + HISTORY_ITEMS_PER_PAGE - 1;

    try {
      if (page === 0) {
        setIsLoadingCompletedTasks(true);
      } else {
        setIsLoadingMoreHistory(true);
      }

      let query = supabase
        .from('cleaning_tasks')
        .select(`
          *,
          profiles!cleaning_tasks_assigned_staff_id_fkey(full_name),
          property_sections(name)
        `)
        .eq('location_id', id)
        .eq('org_id', currentOrgId)
        .eq('status', 'done')
        .order('completed_at', { ascending: false });

      if (historyDateRange.from) {
        query = query.gte('completed_at', historyDateRange.from.toISOString());
      }
      if (historyDateRange.to) {
        const toDate = new Date(historyDateRange.to);
        toDate.setHours(23, 59, 59, 999);
        query = query.lte('completed_at', toDate.toISOString());
      }
      if (historyStaffFilter !== 'all') {
        query = query.or(`assigned_staff_id.eq.${historyStaffFilter},actual_performer_id.eq.${historyStaffFilter}`);
      }

      const { data, error } = await query.range(from, to);

      if (error) {
        console.error('Error fetching completed tasks:', error);
        toast.error(t('admin.property.history.fetchError'));
        return;
      }

      // Batch fetch performer names (actual_performer_id has priority over assigned_staff_id)
      const tasksRaw = data || [];
      const performerIds = [...new Set(
        tasksRaw
          .map((t) => t.actual_performer_id ?? t.assigned_staff_id)
          .filter(Boolean)
      )] as string[];

      let performerNamesMap: Record<string, string> = {};
      if (performerIds.length > 0) {
        const { data: profilesData } = await supabase
          .from('profiles')
          .select('id, full_name')
          .in('id', performerIds);
        performerNamesMap = Object.fromEntries(
          (profilesData || []).map((p) => [p.id, p.full_name || ''])
        );
      }

      const enrichedTasks = tasksRaw.map((task) => {
        const performerId = task.actual_performer_id ?? task.assigned_staff_id;
        const profileFromJoin = task.profiles as { full_name?: string | null } | { full_name?: string | null }[] | null;
        const assignedName = profileFromJoin
          ? (Array.isArray(profileFromJoin) ? profileFromJoin[0]?.full_name : profileFromJoin.full_name) ?? null
          : null;
        const performerName = performerNamesMap[performerId || ''] ?? assignedName ?? null;
        return { ...task, performerName };
      });

      setHistoryHasMore((data?.length ?? 0) >= HISTORY_ITEMS_PER_PAGE);
      if (append) {
        setCompletedTasks(prev => [...prev, ...enrichedTasks]);
      } else {
        setCompletedTasks(enrichedTasks);
      }
    } catch (error) {
      console.error('Unexpected error:', error);
      toast.error(t('admin.property.toasts.unexpected'));
    } finally {
      setIsLoadingCompletedTasks(false);
      setIsLoadingMoreHistory(false);
    }
  };

  const fetchIssuesHistory = async () => {
    if (!id || !currentOrgId) return;

    try {
      setIsLoadingIssuesHistory(true);

      let query = supabase
        .from('property_issues')
        .select(`
          id,
          created_at,
          description,
          status,
          reporter_id,
          released_from_cleaning_at,
          assigned_staff_id,
          delegated_vendor_id,
          claimed_by_org_id,
          is_public_broadcast,
          reporter:profiles!property_issues_reporter_id_fkey(full_name)
        `)
        .eq('location_id', id)
        .eq('org_id', currentOrgId)
        .eq('source', 'cleaning')
        .neq('status', 'rejected')
        .order('created_at', { ascending: false })
        .limit(50);

      if (issuesHistoryDateRange.from) {
        query = query.gte('created_at', issuesHistoryDateRange.from.toISOString());
      }
      if (issuesHistoryDateRange.to) {
        const toDate = new Date(issuesHistoryDateRange.to);
        toDate.setHours(23, 59, 59, 999);
        query = query.lte('created_at', toDate.toISOString());
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching issues history:', error);
        toast.error(t('admin.property.history.fetchIssuesError'));
        return;
      }

      setIssuesHistory((data as CleaningIssueHistoryRow[]) || []);
    } catch (error) {
      console.error('Unexpected error:', error);
      toast.error(t('admin.property.toasts.unexpected'));
    } finally {
      setIsLoadingIssuesHistory(false);
    }
  };

  const fetchHolidays = async () => {
    if (!currentOrgId) return;

    try {
      setIsLoadingHolidays(true);
      
      // Fetch holidays for this location and global holidays for the organization
      const { data, error } = await supabase
        .from('location_holidays')
        .select('id, org_id, location_id, holiday_date, description, created_at, updated_at')
        .eq('org_id', currentOrgId)
        .or(`location_id.eq.${id},location_id.is.null`)
        .order('holiday_date', { ascending: true });

      if (error) {
        console.error('Error fetching holidays:', error);
        toast.error(t('admin.property.holidays.fetchError'));
        return;
      }

      setHolidays(data || []);
    } catch (error) {
      console.error('Unexpected error:', error);
      toast.error(t('admin.property.toasts.unexpected'));
    } finally {
      setIsLoadingHolidays(false);
    }
  };

  const handleAddHoliday = async () => {
    if (!canEdit || !currentOrgId) {
      toast.error(t('admin.property.toasts.noPermission'));
      return;
    }

    if (!newHolidayDate) {
      toast.error(t('admin.property.holidays.selectDate'));
      return;
    }

    try {
      setIsSaving(true);

      const { error } = await supabase
        .from('location_holidays')
        .insert({
          org_id: currentOrgId,
          location_id: isGlobalHoliday ? null : (id || null),
          holiday_date: newHolidayDate.toISOString().split('T')[0],
          description: newHolidayDescription.trim() || null,
          created_by: (await supabase.auth.getUser()).data.user?.id || null,
        });

      if (error) {
        console.error('Error adding holiday:', error);
        if (error.code === '23505') {
          toast.error(t('admin.property.holidays.alreadyExists'));
        } else {
          toast.error(t('admin.property.holidays.addError'));
        }
        return;
      }

      toast.success(isGlobalHoliday ? t('admin.property.holidays.addedGlobal') : t('admin.property.holidays.added'));
      setIsAddHolidayDialogOpen(false);
      setNewHolidayDate(undefined);
      setNewHolidayDescription('');
      setIsGlobalHoliday(false);
      await fetchHolidays();
    } catch (error) {
      console.error('Unexpected error:', error);
      toast.error(t('admin.property.toasts.unexpected'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteHoliday = async (holidayId: string) => {
    if (!canEdit) {
      toast.error(t('admin.property.toasts.noPermission'));
      return;
    }

    try {
      setIsSaving(true);

      const { error } = await supabase
        .from('location_holidays')
        .delete()
        .eq('id', holidayId);

      if (error) {
        console.error('Error deleting holiday:', error);
        toast.error(t('admin.property.holidays.deleteError'));
        return;
      }

      toast.success(t('admin.property.holidays.deleted'));
      setHolidayToDelete(null);
      await fetchHolidays();
    } catch (error) {
      console.error('Unexpected error:', error);
      toast.error(t('admin.property.toasts.unexpected'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleMarkAsDelivered = async (requestId: string) => {
    if (!canEdit) return;

    try {
      const { error } = await supabase
        .from('material_requests')
        .update({ status: 'delivered' })
        .eq('id', requestId);

      if (error) {
        console.error('Error updating material request:', error);
        toast.error(t('admin.property.warehouse.orderUpdateError'));
        return;
      }

      toast.success(t('admin.property.warehouse.markedDelivered'));
      fetchMaterialRequests();
    } catch (error) {
      console.error('Unexpected error:', error);
      toast.error(t('admin.property.toasts.unexpected'));
    }
  };

  const handleSubmitOrder = async () => {
    if (!id || !currentOrgId || !selectedProductId || !orderQuantity) {
      toast.error(t('admin.property.warehouse.selectProductAndQty'));
      return;
    }

    try {
      setIsSubmittingOrder(true);

      // Get current user
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError || !user) {
        console.error('Error getting user:', authError);
        toast.error(t('admin.property.warehouse.authError'));
        return;
      }

      // Find selected product
      const selectedProduct = inventoryItems.find(item => item.id === selectedProductId);
      if (!selectedProduct) {
        toast.error(t('admin.property.warehouse.productNotFound'));
        return;
      }

      // Check if there's a pending order for this product
      const hasPendingOrder = materialRequests.some(
        req => 
          req.status !== 'delivered' && 
          req.items && 
          Array.isArray(req.items) &&
          req.items.some((item: any) => 
            (item.item_id === selectedProductId || item.id === selectedProductId) ||
            (item.item_name === selectedProduct.item_name || item.name === selectedProduct.item_name)
          )
      );

      if (hasPendingOrder) {
        toast.error(t('admin.property.warehouse.activeOrderExists'));
        return;
      }

      // Create order item
      const orderItem = {
        item_id: selectedProduct.id,
        item_name: selectedProduct.item_name,
        quantity: parseFloat(orderQuantity) || 1,
        unit: selectedProduct.unit || 'szt'
      };

      const { error } = await supabase
        .from('material_requests')
        .insert({
          org_id: currentOrgId,
          location_id: id,
          requester_id: user.id,
          items: [orderItem],
          status: 'pending',
          notes: `Zamówienie: ${selectedProduct.item_name} (${orderQuantity} ${selectedProduct.unit || 'szt'})`,
        });

      if (error) {
        console.error('Error creating material request:', error);
        toast.error(t('admin.property.warehouse.createOrderError'));
        return;
      }

      toast.success(t('admin.property.warehouse.requestSent'));
      setIsOrderDialogOpen(false);
      setSelectedProductId('');
      setOrderQuantity('1');
      fetchMaterialRequests();
    } catch (error) {
      console.error('Unexpected error:', error);
      toast.error(t('admin.property.toasts.unexpected'));
    } finally {
      setIsSubmittingOrder(false);
    }
  };

  const getTaskStatusBadge = (status: string | null) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline">{t('admin.property.taskStatus.pending')}</Badge>;
      case 'in_progress':
        return <Badge variant="default" className="bg-blue-500">{t('admin.property.taskStatus.inProgress')}</Badge>;
      case 'done':
        return <Badge variant="default" className="bg-green-500">{t('admin.property.taskStatus.done')}</Badge>;
      default:
        return <Badge variant="outline">{t('admin.property.taskStatus.pending')}</Badge>;
    }
  };

  /** Effective status for display: checklist (JSONB) overrides SQL status column. */
  const getEffectiveTaskStatus = (task: CleaningTask): string | null => {
    const raw = task.checklist;
    if (raw != null) {
      if (typeof raw === 'object' && raw !== null && !Array.isArray(raw) && 'completed' in raw) {
        if ((raw as { completed?: boolean }).completed === true) return 'done';
      }
      let arr: Array<{ completed?: boolean }> = [];
      if (Array.isArray(raw)) {
        arr = raw as Array<{ completed?: boolean }>;
      } else if (typeof raw === 'object' && raw !== null) {
        arr = Object.values(raw).filter((v): v is { completed?: boolean } => typeof v === 'object' && v !== null) as Array<{ completed?: boolean }>;
      }
      if (arr.length > 0) {
        const allCompleted = arr.every((item) => item.completed === true);
        return allCompleted ? 'done' : (task.status ?? 'pending');
      }
    }
    return task.status ?? 'pending';
  };

  /** True if task is completed and completed_at is today (or null = just completed, show anyway). */
  const isTaskCompletedToday = (task: CleaningTask): boolean => {
    const at = task.completed_at;
    if (!at) return true; // no date = show (e.g. just checked by employee)
    const d = new Date(at);
    const today = new Date();
    return d.getFullYear() === today.getFullYear() && d.getMonth() === today.getMonth() && d.getDate() === today.getDate();
  };

  /** Task type label for display (ujednolicone dla listy i modala). */
  const getTaskTypeLabel = (taskType: string | null): string => {
    switch (taskType) {
      case 'sop_standard': return t('admin.property.taskType.standard');
      case 'coordinator_single': return t('admin.property.taskType.coordinatorSingle');
      case 'employee_extra': return t('admin.property.taskType.employeeExtra');
      case 'long_term': return t('admin.property.taskType.longTerm');
      default: return t('admin.property.task');
    }
  };

  /** Whether task is completed (for counters) – uses checklist/status, not only SQL status. */
  const checkIsTaskCompleted = (task: CleaningTask): boolean => getEffectiveTaskStatus(task) === 'done';

  /** Extract notes and photo URLs from task checklist (spłaszczone z wszystkich elementów). */
  const getTaskChecklistExtra = (task: CleaningTask): { allPhotos: string[]; allNotes: string[] } => {
    const allPhotos: string[] = [];
    const allNotes: string[] = [];
    const items = parseChecklistItems(task.checklist);
    for (const item of items) {
      if (item.photo_urls) allPhotos.push(...item.photo_urls);
      if (item.note?.trim()) allNotes.push(item.note.trim());
    }
    const taskNote = task.staff_notes;
    if (taskNote && taskNote.trim()) allNotes.push(taskNote.trim());
    return { allPhotos, allNotes };
  };

  /** Whether any checklist item requires photo verification. */
  const taskRequiresPhoto = (task: CleaningTask): boolean => {
    return parseChecklistItems(task.checklist).some(
      (item) => item.photo_required || item.requires_photo
    );
  };

  const calculateNextExecution = (task: PropertyChecklist): string => {
    // Use the service function which handles baseline_date correctly
    const nextDate = calculateNextExecutionDate(task);
    if (nextDate) {
      return nextDate;
    }
    
    // Fallback if service returns null
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  const sortedScheduleChecklists = useMemo(() => {
    const { key, direction } = scheduleSortConfig;
    if (!key) return scheduleChecklists;
    const mult = direction === 'asc' ? 1 : -1;
    return [...scheduleChecklists].sort((a, b) => {
      if (key === 'task') {
        const na = (a.name || t('admin.property.noName')).toLowerCase();
        const nb = (b.name || t('admin.property.noName')).toLowerCase();
        return mult * na.localeCompare(nb);
      }
      if (key === 'section') {
        const sa = getScheduleSectionName(a);
        const sb = getScheduleSectionName(b);
        return mult * sa.localeCompare(sb);
      }
      if (key === 'frequency') {
        const oa = getScheduleFrequencyOrder(a);
        const ob = getScheduleFrequencyOrder(b);
        if (oa !== ob) return mult * (oa - ob);
        return mult * formatFrequencyDisplay(a).localeCompare(formatFrequencyDisplay(b));
      }
      if (key === 'nextExecution') {
        const da = new Date(calculateNextExecution(a)).getTime();
        const db = new Date(calculateNextExecution(b)).getTime();
        return mult * (da - db);
      }
      if (key === 'status') {
        const va = a.is_active ? 1 : 0;
        const vb = b.is_active ? 1 : 0;
        return mult * (va - vb); // active first when asc
      }
      return 0;
    });
  }, [scheduleChecklists, scheduleSortConfig, i18n.language]);

  const handleToggleTaskStatus = async (task: PropertyChecklist) => {
    if (!canEdit || !task.id) return;

    try {
      setIsSaving(true);
      const newStatus = !task.is_active;

      const { error } = await supabase
        .from('property_checklists')
        .update({ is_active: newStatus })
        .eq('id', task.id);

      if (error) {
        console.error('Error updating task status:', error);
        toast.error(t('admin.property.schedule.statusUpdateError'));
        return;
      }

      toast.success(newStatus ? t('admin.property.schedule.activated') : t('admin.property.schedule.paused'));
      await fetchScheduleChecklists();
    } catch (error) {
      console.error('Unexpected error:', error);
      toast.error(t('admin.property.toasts.unexpected'));
    } finally {
      setIsSaving(false);
    }
  };


  const handleGenerateNow = async (task: PropertyChecklist) => {
    if (!canEdit || !task.id) {
      toast.error(t('admin.property.toasts.noPermission'));
      return;
    }

    if (!task.location_id || !task.org_id) {
      toast.error(t('admin.property.schedule.missingPropertyData'));
      return;
    }

    try {
      setIsSaving(true);
      
      // Generate tasks for next 30 days (configurable)
      const results = await generateTasksForTemplate(task, { daysAhead: 30 });

      if (results.errors > 0) {
        toast.error(t('admin.property.schedule.generatedWithErrors', { created: results.created, skipped: results.skipped, errors: results.errors }));
      } else if (results.created > 0) {
        toast.success(results.skipped > 0 ? t('admin.property.schedule.generatedWithSkipped', { created: results.created, skipped: results.skipped }) : t('admin.property.schedule.generated', { created: results.created }));
      } else if (results.skipped > 0) {
        toast.info(t('admin.property.schedule.allExist', { skipped: results.skipped }));
      } else {
        toast.info(t('admin.property.schedule.nothingToGenerate'));
      }

      // Refresh schedule checklists to show newly generated tasks
      await fetchScheduleChecklists();
    } catch (error) {
      console.error('Error generating tasks:', error);
      toast.error(t('admin.property.schedule.generateError'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddSingleTask = async () => {
    if (!canEdit || !location?.id || !currentOrgId) {
      toast.error(t('admin.property.tasks.noPermissionOrData'));
      return;
    }

    if (!singleTaskSectionId || !singleTaskName.trim()) {
      toast.error(t('admin.property.tasks.fillRequired'));
      return;
    }

    try {
      setIsSaving(true);

      // Get tomorrow's date at 9:00 AM as default scheduled_at
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(9, 0, 0, 0);

      // Nazwa zadania bez sztucznych prefixów – flaga requires_photo w checklist wystarczy
      const coordinatorNotes = `Zadanie jednorazowe: ${singleTaskName.trim()}`;

      // Build checklist JSONB with requires_photo flag
      const checklist = {
        requires_photo: singleTaskRequiresPhoto,
      };

      const { error } = await supabase
        .from('cleaning_tasks')
        .insert({
          org_id: currentOrgId,
          location_id: location.id,
          section_id: singleTaskSectionId,
          task_type: 'coordinator_single',
          status: 'pending',
          priority: 'medium',
          assigned_staff_id: null,
          scheduled_at: tomorrow.toISOString(),
          coordinator_notes: coordinatorNotes,
          checklist: checklist,
        });

      if (error) {
        console.error('Error adding single task:', error);
        toast.error(t('admin.property.tasks.addError'));
        return;
      }

      toast.success(t('admin.property.tasks.singleAdded'));
      setIsAddSingleTaskDialogOpen(false);
      setSingleTaskSectionId('');
      setSingleTaskName('');
      setSingleTaskRequiresPhoto(false);
      // Odśwież Widok bieżący i liczniki
      await fetchCurrentViewTasks();
    } catch (error) {
      console.error('Unexpected error:', error);
      toast.error(t('admin.property.toasts.unexpected'));
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading && !isLoadError) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (isLoadError) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center space-y-4">
          <p className="text-lg font-medium text-muted-foreground">
            {t('admin.property.header.loadFailed')}
          </p>
          <div className="flex gap-2 justify-center">
            <Button onClick={() => { setIsLoadError(false); fetchPropertyData(); }} variant="default">
              <RefreshCw className="h-4 w-4 mr-2" />
              {t('common.tryAgain')}
            </Button>
            <Button onClick={() => window.location.reload()} variant="outline">
              {t('admin.property.header.refreshPage')}
            </Button>
          </div>
          <Button onClick={() => navigate('/dashboard')} variant="ghost" className="mt-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('admin.property.header.backToDashboard')}
          </Button>
        </div>
      </div>
    );
  }

  if (!location) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <p className="text-lg font-medium mb-2">{t('admin.property.header.notFound')}</p>
          <Button onClick={() => navigate('/dashboard')} variant="outline">
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('admin.property.header.back')}
          </Button>
        </div>
      </div>
    );
  }

  // Extract client data - handle both array and object from JOIN
  let client: CleaningClient | null = null;
  if (location.cleaning_clients) {
    if (Array.isArray(location.cleaning_clients)) {
      client = location.cleaning_clients[0] || null;
    } else {
      client = location.cleaning_clients as CleaningClient;
    }
  }
  const contractDetails = (client?.contract_details as ContractDetails) || {};

  // Build Google Maps URL
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location.address)}`;

  return (
    <div className="space-y-6 bg-slate-100 dark:bg-slate-950 min-h-screen -m-6 p-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('admin.property.header.back')}
          </Button>
          <div>
            <h1 className="text-3xl font-bold">{location.address}</h1>
            <div className="flex items-center gap-2 mt-2 text-muted-foreground">
              <MapPin className="h-4 w-4" />
              <span className="text-sm">{location.address}</span>
              <a
                href={mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-primary hover:underline ml-2"
              >
                {t('admin.property.header.openInMaps')}
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>
            {/* Financial Panel */}
            {canViewFinancials && (
              <div className="flex items-center gap-6 mt-4 p-3 bg-white dark:bg-slate-900 rounded-lg border border-border/60">
                {location.monthly_salary !== null && location.monthly_salary !== undefined && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-muted-foreground">{t('admin.property.header.monthlySalary')}</span>
                    <span className="text-sm font-semibold">{location.monthly_salary.toFixed(2)} {t('admin.property.currency')}</span>
                  </div>
                )}
                {location.cleaning_rate !== null && location.cleaning_rate !== undefined && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-muted-foreground">{t('admin.property.header.cleaningRate')}</span>
                    <span className="text-sm font-semibold">{location.cleaning_rate.toFixed(2)} {t('admin.property.currency')}</span>
                  </div>
                )}
                {location.snow_removal_rate !== null && location.snow_removal_rate !== undefined && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-muted-foreground">{t('admin.property.header.snowRate')}</span>
                    <span className="text-sm font-semibold">{location.snow_removal_rate.toFixed(2)} {t('admin.property.currency')}</span>
                  </div>
                )}
                {location.extra_work_rate !== null && location.extra_work_rate !== undefined && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-muted-foreground">{t('admin.property.header.extraRate')}</span>
                    <span className="text-sm font-semibold">{location.extra_work_rate.toFixed(2)} {t('admin.property.currency')}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {canEdit && (
            <Button
              onClick={() => setIsAddSingleTaskDialogOpen(true)}
              className="flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              {t('admin.property.header.addTask')}
            </Button>
          )}
          {canEdit && (
            <Button
              variant="outline"
              onClick={() => setIsExtraPaidOpen(true)}
              className="flex items-center gap-2"
            >
              <Wallet className="h-4 w-4" />
              {t('admin.property.header.extraWork')}
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="administration" className="w-full">
        <TabsList>
          <TabsTrigger value="administration">{t('admin.property.tabs.administration')}</TabsTrigger>
          <TabsTrigger value="current-view">{t('admin.property.tabs.currentView')}</TabsTrigger>
          <TabsTrigger value="sections">{t('admin.property.tabs.sections')}</TabsTrigger>
          <TabsTrigger value="schedule">{t('admin.property.tabs.schedule')}</TabsTrigger>
          <TabsTrigger value="warehouse">{t('admin.property.tabs.warehouse')}</TabsTrigger>
          <TabsTrigger value="holidays">{t('admin.property.tabs.holidays')}</TabsTrigger>
        </TabsList>

        {/* Administration Tab */}
        <TabsContent value="administration" forceMount className="space-y-6 mt-6 data-[state=inactive]:hidden">
          {canEdit && currentOrgId && id ? (
            <ContractorAttachCard
              orgId={currentOrgId}
              cleaningLocationId={id}
              flags={{ isCleaning: true }}
              allowedKinds={[...HOUSING_KINDS, ...COMPANY_KINDS]}
            />
          ) : null}
          {/* Administrator Information Card */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <User className="h-5 w-5 text-muted-foreground" />
                  <CardTitle>{t('admin.property.client.title')}</CardTitle>
                </div>
                {canEdit && (
                  <>
                    {isEditingClient ? (
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleCancelEditClient}
                          disabled={isSaving}
                          className="flex items-center gap-2"
                        >
                          <X className="h-4 w-4" />
                          {t('common.cancel')}
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleSaveClient}
                          disabled={isSaving}
                          className="flex items-center gap-2"
                        >
                          <Save className="h-4 w-4" />
                          {t('common.save')}
                        </Button>
                      </div>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleEditClient}
                        className="flex items-center gap-2"
                      >
                        <Edit className="h-4 w-4" />
                        {t('admin.property.edit')}
                      </Button>
                    )}
                  </>
                )}
              </div>
              <CardDescription>
                {t('admin.property.client.description')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {(client || isEditingClient) ? (
                <div className="space-y-4">
                  {/* Name */}
                  <div className="grid grid-cols-[160px_1fr] items-center gap-4">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{t('admin.property.client.fullName')}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {isEditingClient ? (
                        <Input
                          value={editedClientData.name || ''}
                          onChange={(e) =>
                            setEditedClientData({ ...editedClientData, name: e.target.value })
                          }
                          className="flex-1"
                          placeholder={t('admin.property.client.fullName')}
                        />
                      ) : (
                        <>
                          <span className="bg-slate-200/50 dark:bg-slate-900/50 border border-border/60 rounded-lg p-3 flex-1">
                            {client?.name || t('admin.property.none')}
                          </span>
                          {contractDetails.nip && (
                            <span className="text-sm bg-slate-200/50 dark:bg-slate-900/50 border border-border/60 rounded-lg p-3 whitespace-nowrap">
                              NIP: {contractDetails.nip}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* NIP - only in edit mode */}
                  {isEditingClient && (
                    <div className="grid grid-cols-[160px_1fr] items-center gap-4">
                      <span className="font-medium">{t('admin.property.client.nip')}</span>
                      <Input
                        value={editedClientData.nip || ''}
                        onChange={(e) =>
                          setEditedClientData({ ...editedClientData, nip: e.target.value })
                        }
                        className="flex-1"
                        placeholder={t('admin.property.client.nipPlaceholder')}
                      />
                    </div>
                  )}

                  {/* Email */}
                  <div className="grid grid-cols-[160px_1fr] items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{t('admin.property.client.email')}</span>
                    </div>
                    {isEditingClient ? (
                      <Input
                        value={editedClientData.email || ''}
                        onChange={(e) =>
                          setEditedClientData({ ...editedClientData, email: e.target.value })
                        }
                        type="email"
                        className="flex-1"
                        placeholder={t('admin.property.client.emailPlaceholder')}
                      />
                    ) : (
                      <span className="bg-slate-200/50 dark:bg-slate-900/50 border border-border/60 rounded-lg p-3">
                        {client?.email || t('admin.property.none')}
                      </span>
                    )}
                  </div>

                  {/* Phone */}
                  <div className="grid grid-cols-[160px_1fr] items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{t('admin.property.client.phone')}</span>
                    </div>
                    {isEditingClient ? (
                      <Input
                        value={editedClientData.phone || ''}
                        onChange={(e) =>
                          setEditedClientData({ ...editedClientData, phone: e.target.value })
                        }
                        type="tel"
                        className="flex-1"
                        placeholder={t('admin.property.client.phonePlaceholder')}
                      />
                    ) : (
                      <span className="bg-slate-200/50 dark:bg-slate-900/50 border border-border/60 rounded-lg p-3">
                        {client?.phone || t('admin.property.none')}
                      </span>
                    )}
                  </div>

                  {/* Address */}
                  <div className="grid grid-cols-[160px_1fr] items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Building className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{t('admin.property.client.address')}</span>
                    </div>
                    {isEditingClient ? (
                      <Input
                        value={editedClientData.address || ''}
                        onChange={(e) =>
                          setEditedClientData({ ...editedClientData, address: e.target.value })
                        }
                        className="flex-1"
                        placeholder={t('admin.property.client.addressPlaceholder')}
                      />
                    ) : (
                      <span className="bg-slate-200/50 dark:bg-slate-900/50 border border-border/60 rounded-lg p-3">
                        {contractDetails.address || t('admin.property.none')}
                      </span>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-muted-foreground bg-slate-200/50 dark:bg-slate-900/50 border border-border/60 rounded-lg p-3">
                  {t('admin.property.client.noClient')}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Access Conditions Card */}
          <Card>
            <CardHeader>
              <CardTitle>{t('admin.property.access.title')}</CardTitle>
              <CardDescription>
                {t('admin.property.access.description')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="bg-slate-200/50 dark:bg-slate-900/50 border border-border/60 rounded-lg p-3 space-y-6">
              {/* GPS Verification */}
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="gps-verification" className="text-base font-medium">
                    {t('admin.property.access.gps')}
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {t('admin.property.access.gpsHint')}
                  </p>
                </div>
                {canEdit ? (
                  <Switch
                    id="gps-verification"
                    checked={validationConfig.gps_required}
                    onCheckedChange={(checked) =>
                      handleValidationToggle('gps_required', checked)
                    }
                    disabled={isSaving}
                  />
                ) : (
                  <Switch
                    id="gps-verification"
                    checked={validationConfig.gps_required}
                    disabled={true}
                  />
                )}
              </div>

              {/* QR Code Verification */}
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="qr-verification" className="text-base font-medium">
                    {t('admin.property.access.qr')}
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {t('admin.property.access.qrHint')}
                  </p>
                </div>
                {canEdit ? (
                  <Switch
                    id="qr-verification"
                    checked={validationConfig.qr_required}
                    onCheckedChange={(checked) =>
                      handleValidationToggle('qr_required', checked)
                    }
                    disabled={isSaving}
                  />
                ) : (
                  <Switch
                    id="qr-verification"
                    checked={validationConfig.qr_required}
                    disabled={true}
                  />
                )}
              </div>

              {/* Biometric Verification */}
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="biometric-verification" className="text-base font-medium">
                    {t('admin.property.access.biometric')}
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {t('admin.property.access.biometricHint')}
                  </p>
                </div>
                {canEdit ? (
                  <Switch
                    id="biometric-verification"
                    checked={validationConfig.biometric_required}
                    onCheckedChange={(checked) =>
                      handleValidationToggle('biometric_required', checked)
                    }
                    disabled={isSaving}
                  />
                ) : (
                  <Switch
                    id="biometric-verification"
                    checked={validationConfig.biometric_required}
                    disabled={true}
                  />
                )}
              </div>
              </div>
            </CardContent>
          </Card>

          {/* QR Verification System Card */}
          <Card>
            <CardHeader>
              <CardTitle>{t('admin.property.qr.systemTitle')}</CardTitle>
              <CardDescription>
                {t('admin.property.qr.systemDescription')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="bg-slate-200/50 dark:bg-slate-900/50 border border-border/60 rounded-lg p-3 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-base font-medium">{t('admin.property.qr.tokenStatus')}</Label>
                    <p className="text-sm text-muted-foreground">
                      {location?.qr_code_token ? (
                        <span className="font-mono text-xs break-all">
                          {t('admin.property.qr.token', { token: location.qr_code_token })}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">{t('admin.property.qr.noCode')}</span>
                      )}
                    </p>
                  </div>
                </div>
                {canEdit && (
                  <Button
                    onClick={() => setIsQRManagementDialogOpen(true)}
                    className="w-full"
                    variant="outline"
                  >
                    <QrCode className="mr-2 h-4 w-4" />
                    {t('admin.property.qr.manage')}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Entry Instructions Card */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>{t('admin.property.instructions.title')}</CardTitle>
                {canEdit && (
                  <>
                    {isEditingInstructions ? (
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleCancelEditInstructions}
                          disabled={isSaving}
                          className="flex items-center gap-2"
                        >
                          <X className="h-4 w-4" />
                          {t('common.cancel')}
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleSaveInstructions}
                          disabled={isSaving}
                          className="flex items-center gap-2"
                        >
                          <Save className="h-4 w-4" />
                          {t('common.save')}
                        </Button>
                      </div>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleEditInstructions}
                        className="flex items-center gap-2"
                      >
                        <Edit className="h-4 w-4" />
                        {t('admin.property.edit')}
                      </Button>
                    )}
                  </>
                )}
              </div>
              <CardDescription>
                {t('admin.property.instructions.description')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Entry Instructions */}
              <div className="grid grid-cols-[160px_1fr] items-start gap-4">
                <h4 className="font-medium pt-2">{t('admin.property.instructions.title')}</h4>
                <div>
                  {isEditingInstructions ? (
                    <div className="space-y-3">
                      <div>
                        <Label htmlFor="access-code" className="text-sm font-medium mb-1 block">
                          {t('admin.property.instructions.accessCode')}
                        </Label>
                        <Input
                          id="access-code"
                          value={editedInstructions.access_code || ''}
                          onChange={(e) =>
                            setEditedInstructions({
                              ...editedInstructions,
                              access_code: e.target.value,
                            })
                          }
                          placeholder={t('admin.property.instructions.accessCode')}
                          className="w-full"
                        />
                      </div>
                      <div>
                        <Label htmlFor="instruction-notes" className="text-sm font-medium mb-1 block">
                          {t('admin.property.instructions.label')}
                        </Label>
                        <Textarea
                          id="instruction-notes"
                          value={editedInstructions.instruction_notes || ''}
                          onChange={(e) =>
                            setEditedInstructions({
                              ...editedInstructions,
                              instruction_notes: e.target.value,
                            })
                          }
                          placeholder={t('admin.property.instructions.placeholder')}
                          className="w-full min-h-[100px]"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm bg-slate-200/50 dark:bg-slate-900/50 border border-border/60 rounded-lg p-3 space-y-1">
                      {location.access_code && (
                        <p>{t('admin.property.instructions.accessCodeValue', { code: location.access_code })}</p>
                      )}
                      {location.instruction_notes ? (
                        <div className="space-y-1">
                          {location.instruction_notes.split('\n').map((line, index) => (
                            <p key={index}>{line}</p>
                          ))}
                        </div>
                      ) : (
                        <p className="text-muted-foreground">{t('admin.property.instructions.none')}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Coordinator Notes */}
              <div className="grid grid-cols-[160px_1fr] items-start gap-4">
                <h4 className="font-medium pt-2">{t('admin.property.instructions.coordinatorNotes')}</h4>
                {isEditingInstructions ? (
                  <Textarea
                    value={editedInstructions.coordinator_notes || ''}
                    onChange={(e) =>
                      setEditedInstructions({
                        ...editedInstructions,
                        coordinator_notes: e.target.value,
                      })
                    }
                    placeholder={t('admin.property.instructions.coordinatorPlaceholder')}
                    className="w-full min-h-[80px]"
                  />
                ) : (
                  <p className="text-sm bg-slate-200/50 dark:bg-slate-900/50 border border-border/60 rounded-lg p-3">
                    {location.coordinator_notes || t('admin.property.noNotes')}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Financial Data Card — coordinators only when allowed to view; visibility flag is owner-only */}
          {(isOwner || canViewFinancials) && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>{t('admin.property.finance.title')}</CardTitle>
                    <CardDescription>
                      {t('admin.property.finance.description')}
                    </CardDescription>
                  </div>
                  {canEdit && (isEditingFinancials ? (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleCancelEditFinancials}
                        disabled={isSaving}
                        className="flex items-center gap-2"
                      >
                        <X className="h-4 w-4" />
                        {t('common.cancel')}
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleSaveFinancials}
                        disabled={isSaving}
                        className="flex items-center gap-2"
                      >
                        <Save className="h-4 w-4" />
                        {t('common.save')}
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleEditFinancials}
                      className="flex items-center gap-2"
                    >
                      <Edit className="h-4 w-4" />
                      {t('admin.property.edit')}
                    </Button>
                  ))}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Monthly Fee */}
                <div className="grid grid-cols-[200px_1fr] items-center gap-4">
                  <Label htmlFor="monthly-salary" className="font-medium">
                    {t('admin.property.finance.monthlyFee')}
                  </Label>
                  {isEditingFinancials ? (
                    <Input
                      id="monthly-salary"
                      type="number"
                      step="0.01"
                      value={editedFinancialData.monthly_salary || ''}
                      onChange={(e) =>
                        setEditedFinancialData({
                          ...editedFinancialData,
                          monthly_salary: parseFloat(e.target.value) || 0,
                        })
                      }
                      className="flex-1"
                      placeholder="0.00"
                    />
                  ) : (
                    <span className="bg-slate-200/50 dark:bg-slate-900/50 border border-border/60 rounded-lg p-3">
                      {location?.monthly_salary ? `${location.monthly_salary.toFixed(2)} ${t('admin.property.currency')}` : t('admin.property.none')}
                    </span>
                  )}
                </div>

                {/* Cleaning Rate */}
                <div className="grid grid-cols-[200px_1fr] items-center gap-4">
                  <Label htmlFor="cleaning-rate" className="font-medium">
                    {t('admin.property.finance.hourlyCleaning')}
                  </Label>
                  {isEditingFinancials ? (
                    <Input
                      id="cleaning-rate"
                      type="number"
                      step="0.01"
                      value={editedFinancialData.cleaning_rate || ''}
                      onChange={(e) =>
                        setEditedFinancialData({
                          ...editedFinancialData,
                          cleaning_rate: parseFloat(e.target.value) || 0,
                        })
                      }
                      className="flex-1"
                      placeholder="0.00"
                    />
                  ) : (
                    <span className="bg-slate-200/50 dark:bg-slate-900/50 border border-border/60 rounded-lg p-3">
                      {location?.cleaning_rate ? `${location.cleaning_rate.toFixed(2)} ${t('admin.property.currency')}` : t('admin.property.none')}
                    </span>
                  )}
                </div>

                {/* Snow Removal Rate */}
                <div className="grid grid-cols-[200px_1fr] items-center gap-4">
                  <Label htmlFor="snow-removal-rate" className="font-medium">
                    {t('admin.property.finance.hourlySnow')}
                  </Label>
                  {isEditingFinancials ? (
                    <Input
                      id="snow-removal-rate"
                      type="number"
                      step="0.01"
                      value={editedFinancialData.snow_removal_rate || ''}
                      onChange={(e) =>
                        setEditedFinancialData({
                          ...editedFinancialData,
                          snow_removal_rate: parseFloat(e.target.value) || 0,
                        })
                      }
                      className="flex-1"
                      placeholder="0.00"
                    />
                  ) : (
                    <span className="bg-slate-200/50 dark:bg-slate-900/50 border border-border/60 rounded-lg p-3">
                      {location?.snow_removal_rate ? `${location.snow_removal_rate.toFixed(2)} ${t('admin.property.currency')}` : t('admin.property.none')}
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-[200px_1fr] items-center gap-4">
                  <Label htmlFor="extra-work-rate" className="font-medium">
                    {t('admin.property.finance.hourlyExtra')}
                  </Label>
                  {isEditingFinancials ? (
                    <Input
                      id="extra-work-rate"
                      type="number"
                      step="0.01"
                      value={editedFinancialData.extra_work_rate || ''}
                      onChange={(e) =>
                        setEditedFinancialData({
                          ...editedFinancialData,
                          extra_work_rate: parseFloat(e.target.value) || 0,
                        })
                      }
                      className="flex-1"
                      placeholder="0.00"
                    />
                  ) : (
                    <span className="bg-slate-200/50 dark:bg-slate-900/50 border border-border/60 rounded-lg p-3">
                      {location?.extra_work_rate ? `${location.extra_work_rate.toFixed(2)} ${t('admin.property.currency')}` : t('admin.property.none')}
                    </span>
                  )}
                </div>

                {isOwner && (
                  <div className="flex items-center justify-between pt-4 border-t">
                    <div className="space-y-0.5">
                      <Label htmlFor="coordinator-financials" className="text-base font-medium">
                        {t('admin.property.finance.coordinatorSees')}
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        {t('admin.property.finance.coordinatorSeesHint')}
                      </p>
                    </div>
                    <Switch
                      id="coordinator-financials"
                      checked={location?.coordinator_can_view_financials || false}
                      onCheckedChange={handleToggleCoordinatorFinancials}
                      disabled={isSaving}
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Issue Notifications Card */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Settings className="h-4 w-4 text-muted-foreground" />
                <CardTitle>{t('admin.property.notifications.title')}</CardTitle>
              </div>
              <CardDescription>
                {t('admin.property.notifications.description')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Auto Notify Switch */}
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="auto-notify-issues" className="text-base font-medium">
                    {t('admin.property.notifications.autoForward')}
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {t('admin.property.notifications.autoForwardHint')}
                  </p>
                </div>
                {canEdit ? (
                  <Switch
                    id="auto-notify-issues"
                    checked={location?.auto_notify_issues || false}
                    onCheckedChange={handleToggleAutoNotifyIssues}
                    disabled={isSaving}
                  />
                ) : (
                  <Switch
                    id="auto-notify-issues"
                    checked={location?.auto_notify_issues || false}
                    disabled={true}
                  />
                )}
              </div>

              {/* Notification Email Input */}
              <div className="space-y-2">
                <Label htmlFor="client-notification-email" className="text-base font-medium">
                  {t('admin.property.notifications.emailLabel')}
                </Label>
                <p className="text-sm text-muted-foreground">
                  {t('admin.property.notifications.emailHint')}
                </p>
                {canEdit ? (
                  <Input
                    id="client-notification-email"
                    type="email"
                    value={location?.client_notification_email || ''}
                    onChange={(e) => {
                      // Update local state immediately for better UX
                      if (location) {
                        setLocation({
                          ...location,
                          client_notification_email: e.target.value || null,
                        });
                      }
                    }}
                    onBlur={(e) => {
                      // Save on blur
                      handleSaveNotificationEmail(e.target.value);
                    }}
                    onKeyDown={(e) => {
                      // Save on Enter
                      if (e.key === 'Enter') {
                        e.currentTarget.blur();
                      }
                    }}
                    placeholder={t('admin.property.notifications.emailPlaceholder')}
                    className="w-full"
                    disabled={isSaving}
                  />
                ) : (
                  <span className="bg-slate-200/50 dark:bg-slate-900/50 border border-border/60 rounded-lg p-3 block">
                    {location?.client_notification_email || t('admin.property.notifications.noEmail')}
                  </span>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Archive Property Card */}
          {canEdit && (
            <Card>
              <CardHeader>
                <CardTitle>{t('admin.property.propertyMgmt.title')}</CardTitle>
                <CardDescription>
                  {t('admin.property.propertyMgmt.description')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {location?.status === 'archived' ? (
                    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 space-y-3">
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="bg-amber-500 text-white">
                          {t('admin.property.archived')}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {t('admin.property.propertyMgmt.archivedNotice')}
                      </p>
                      <Button
                        onClick={handleRestoreProperty}
                        disabled={isSaving}
                        className="w-full"
                        variant="outline"
                      >
                        {isSaving ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            {t('admin.property.restoring')}
                          </>
                        ) : (
                          <>
                            <RotateCcw className="h-4 w-4 mr-2" />
                            {t('admin.property.propertyMgmt.restore')}
                          </>
                        )}
                      </Button>
                    </div>
                  ) : (
                    <div className="bg-slate-200/50 dark:bg-slate-900/50 border border-border/60 rounded-lg p-4 space-y-3">
                      <p className="text-sm text-muted-foreground">
                        {t('admin.property.propertyMgmt.archiveHint')}
                      </p>
                      <Button
                        onClick={handleArchiveProperty}
                        disabled={isSaving}
                        variant="destructive"
                        className="w-full"
                      >
                        {isSaving ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            {t('admin.property.archiving')}
                          </>
                        ) : (
                          <>
                            <Trash2 className="h-4 w-4 mr-2" />
                            {t('admin.property.propertyMgmt.archive')}
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Current View Tab */}
        <TabsContent value="current-view" forceMount className="mt-6 space-y-6 data-[state=inactive]:hidden">
          <Card>
            <CardHeader>
              <CardTitle>{t('admin.property.tabs.currentView')}</CardTitle>
              <CardDescription>
                {t('admin.property.currentView.description')}
                {currentViewTasks.length > 0 && (
                  <span className="ml-2 text-sm font-medium">
                    {t('admin.property.currentView.completed', { done: currentViewTasks.filter(checkIsTaskCompleted).length, total: currentViewTasks.length })}
                  </span>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoadingCurrentViewTasks ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              ) : currentViewTasks.length === 0 ? (
                <div className="text-center text-muted-foreground py-8">
                  {t('admin.property.currentView.empty')}
                </div>
              ) : (
                <div className="space-y-6">
                  <VirtualizedColumn
                    items={sectionsWithCurrentViewTasks}
                    getKey={(s) => s.id}
                    threshold={50}
                    estimateSize={320}
                    renderItem={(section) => {
                    const sectionTasks = currentViewTasks.filter(
                      (task) => task.section_id === section.id
                    );
                    return (
                      <div
                        className="border border-border/60 rounded-lg overflow-hidden bg-white dark:bg-card"
                      >
                        {/* Section Header */}
                        <div className="bg-slate-800 dark:bg-slate-900 text-white px-4 py-3">
                          <h3 className="font-semibold text-lg">{section.name}</h3>
                        </div>
                        
                        {/* Tasks List */}
                        <div className="p-4 space-y-3">
                          {sectionTasks.map((task) => (
                            <div
                              key={task.id}
                              className={cn(
                                "flex items-center justify-between p-3 rounded-lg border border-border/60 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-900 transition-colors",
                                task.task_type === 'coordinator_single' && "bg-yellow-50 dark:bg-yellow-900/20 border-yellow-300 dark:border-yellow-700"
                              )}
                              onClick={() => {
                                if (currentRole === 'coordinator' || currentRole === 'owner') {
                                  setSelectedTaskDetails(task);
                                  setIsTaskDetailsDialogOpen(true);
                                }
                              }}
                            >
                              <div className="flex items-center gap-3 flex-1">
                                {task.task_type === 'coordinator_single' && (
                                  <Zap className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
                                )}
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium">
                                      {(() => {
                                        if (task.coordinator_notes) {
                                          return task.coordinator_notes;
                                        }
                                        const firstItem = parseChecklistItems(task.checklist)[0];
                                        return firstItem?.name || firstItem?.task_name || t('admin.property.task');
                                      })()}
                                    </span>
                                    {taskRequiresPhoto(task) && (
                                      <TooltipProvider>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <Camera className="h-4 w-4 text-muted-foreground shrink-0" />
                                          </TooltipTrigger>
                                          <TooltipContent>
                                            <p>{t('admin.property.photoRequired')}</p>
                                          </TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                    )}
                                    {getTaskStatusBadge(getEffectiveTaskStatus(task))}
                                  </div>
                                  <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                                    <span>
                                      <CalendarIcon className="h-3 w-3 inline mr-1" />
                                      {task.scheduled_at ? new Date(task.scheduled_at).toLocaleDateString(intlLocale) : t('admin.property.noDate')}
                                    </span>
                                    <Badge variant="secondary" className="text-xs font-normal">
                                      {getTaskTypeLabel(task.task_type)}
                                    </Badge>
                                  </div>
                                  {(() => {
                                    const { allPhotos, allNotes } = getTaskChecklistExtra(task);
                                    if (allNotes.length === 0 && allPhotos.length === 0) return null;
                                    return (
                                      <div className="flex items-center gap-3 mt-2 text-xs">
                                        {allNotes.length > 0 && (
                                          <TooltipProvider>
                                            <Tooltip>
                                              <TooltipTrigger asChild>
                                                <span className="inline-flex items-center gap-1 text-muted-foreground italic">
                                                  <MessageSquare className="h-3.5 w-3.5 shrink-0" />
                                                  <span className="line-clamp-1 max-w-[200px]">
                                                    {allNotes[0]}
                                                    {allNotes.length > 1 && ` (+${allNotes.length - 1})`}
                                                  </span>
                                                </span>
                                              </TooltipTrigger>
                                              <TooltipContent className="max-w-xs">
                                                <p className="font-medium mb-1">{t('admin.property.workerNotes')}</p>
                                                {allNotes.map((n, i) => (
                                                  <p key={i} className="text-sm">{n}</p>
                                                ))}
                                              </TooltipContent>
                                            </Tooltip>
                                          </TooltipProvider>
                                        )}
                                        {allPhotos.length > 0 && (
                                          <div className="flex items-center gap-1">
                                            {allPhotos.map((photoUrl, i) => (
                                              <StoragePhotoLink
                                                key={i}
                                                pathOrUrl={photoUrl}
                                                fallbackBucket="photos"
                                                alt={t('admin.property.photoAlt', { n: i + 1 })}
                                                onClick={(e) => e.stopPropagation()}
                                                className="shrink-0 rounded overflow-hidden border border-border/60 hover:border-primary/50 transition-colors"
                                                imgClassName="w-8 h-8 object-cover"
                                              />
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })()}
                                </div>
                              </div>
                              {(currentRole === 'owner' || currentRole === 'coordinator') && (
                                task.is_paid ? (
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <span className="inline-flex items-center gap-1">
                                          <Badge variant="secondary" className="gap-1 text-xs">
                                            <Lock className="h-3 w-3" />
                                            {t('admin.property.paid')}
                                          </Badge>
                                          <Button variant="ghost" size="icon" className="h-8 w-8" disabled>
                                            <Trash2 className="h-4 w-4 text-muted-foreground" />
                                          </Button>
                                        </span>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>{t('admin.property.paidLocked')}</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                ) : (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setTaskToDelete(task);
                                    }}
                                    title={t('admin.property.deleteTask')}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                )
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  }}
                  />
                  
                  {/* Tasks without section */}
                  {currentViewTasks.filter((task) => !task.section_id).length > 0 && (
                    <div className="border border-border/60 rounded-lg overflow-hidden bg-white dark:bg-card">
                      <div className="bg-slate-800 dark:bg-slate-900 text-white px-4 py-3">
                        <h3 className="font-semibold text-lg">{t('admin.property.noSection')}</h3>
                      </div>
                      <div className="p-4 space-y-3">
                        {currentViewTasks
                          .filter((task) => !task.section_id)
                          .map((task) => (
                            <div
                              key={task.id}
                              className={cn(
                                "flex items-center justify-between p-3 rounded-lg border border-border/60 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-900 transition-colors",
                                task.task_type === 'coordinator_single' && "bg-yellow-50 dark:bg-yellow-900/20 border-yellow-300 dark:border-yellow-700"
                              )}
                              onClick={() => {
                                if (currentRole === 'coordinator' || currentRole === 'owner') {
                                  setSelectedTaskDetails(task);
                                  setIsTaskDetailsDialogOpen(true);
                                }
                              }}
                            >
                              <div className="flex items-center gap-3 flex-1">
                                {task.task_type === 'coordinator_single' && (
                                  <Zap className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
                                )}
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium">
                                      {(() => {
                                        if (task.coordinator_notes) {
                                          return task.coordinator_notes;
                                        }
                                        const firstItem = parseChecklistItems(task.checklist)[0];
                                        return firstItem?.name || firstItem?.task_name || t('admin.property.task');
                                      })()}
                                    </span>
                                    {taskRequiresPhoto(task) && (
                                      <TooltipProvider>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <Camera className="h-4 w-4 text-muted-foreground shrink-0" />
                                          </TooltipTrigger>
                                          <TooltipContent>
                                            <p>{t('admin.property.photoRequired')}</p>
                                          </TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                    )}
                                    {getTaskStatusBadge(getEffectiveTaskStatus(task))}
                                  </div>
                                  <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                                    <span>
                                      <CalendarIcon className="h-3 w-3 inline mr-1" />
                                      {task.scheduled_at ? new Date(task.scheduled_at).toLocaleDateString(intlLocale) : t('admin.property.noDate')}
                                    </span>
                                    <Badge variant="secondary" className="text-xs font-normal">
                                      {getTaskTypeLabel(task.task_type)}
                                    </Badge>
                                  </div>
                                  {(() => {
                                    const { allPhotos, allNotes } = getTaskChecklistExtra(task);
                                    if (allNotes.length === 0 && allPhotos.length === 0) return null;
                                    return (
                                      <div className="flex items-center gap-3 mt-2 text-xs">
                                        {allNotes.length > 0 && (
                                          <TooltipProvider>
                                            <Tooltip>
                                              <TooltipTrigger asChild>
                                                <span className="inline-flex items-center gap-1 text-muted-foreground italic">
                                                  <MessageSquare className="h-3.5 w-3.5 shrink-0" />
                                                  <span className="line-clamp-1 max-w-[200px]">
                                                    {allNotes[0]}
                                                    {allNotes.length > 1 && ` (+${allNotes.length - 1})`}
                                                  </span>
                                                </span>
                                              </TooltipTrigger>
                                              <TooltipContent className="max-w-xs">
                                                <p className="font-medium mb-1">{t('admin.property.workerNotes')}</p>
                                                {allNotes.map((n, i) => (
                                                  <p key={i} className="text-sm">{n}</p>
                                                ))}
                                              </TooltipContent>
                                            </Tooltip>
                                          </TooltipProvider>
                                        )}
                                        {allPhotos.length > 0 && (
                                          <div className="flex items-center gap-1">
                                            {allPhotos.map((photoUrl, i) => (
                                              <StoragePhotoLink
                                                key={i}
                                                pathOrUrl={photoUrl}
                                                fallbackBucket="photos"
                                                alt={t('admin.property.photoAlt', { n: i + 1 })}
                                                onClick={(e) => e.stopPropagation()}
                                                className="shrink-0 rounded overflow-hidden border border-border/60 hover:border-primary/50 transition-colors"
                                                imgClassName="w-8 h-8 object-cover"
                                              />
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })()}
                                </div>
                              </div>
                              {(currentRole === 'owner' || currentRole === 'coordinator') && (
                                task.is_paid ? (
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <span className="inline-flex items-center gap-1">
                                          <Badge variant="secondary" className="gap-1 text-xs">
                                            <Lock className="h-3 w-3" />
                                            {t('admin.property.paid')}
                                          </Badge>
                                          <Button variant="ghost" size="icon" className="h-8 w-8" disabled>
                                            <Trash2 className="h-4 w-4 text-muted-foreground" />
                                          </Button>
                                        </span>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>{t('admin.property.paidLocked')}</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                ) : (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setTaskToDelete(task);
                                    }}
                                    title={t('admin.property.deleteTask')}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                )
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sections and Personnel Tab */}
        <TabsContent value="sections" forceMount className="mt-6 space-y-6 data-[state=inactive]:hidden">
          {/* Sections Panel */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>{t('admin.property.sections.title')}</CardTitle>
                  <CardDescription>{t('admin.property.sections.description')}</CardDescription>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="show-archived-sections"
                      checked={showArchivedSections}
                      onCheckedChange={setShowArchivedSections}
                    />
                    <Label htmlFor="show-archived-sections" className="text-sm font-normal cursor-pointer">
                      {t('admin.property.sections.showArchived')}
                    </Label>
                  </div>
                  {canEdit && (
                    <Button
                      variant="outline"
                      onClick={() => setIsAddSectionDialogOpen(true)}
                      className="flex items-center gap-2"
                    >
                      <Plus className="h-4 w-4" />
                      {t('admin.property.sections.add')}
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {isLoadingSections ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              ) : sections.length === 0 ? (
                <div className="text-center text-muted-foreground py-8">
                  {t('admin.property.sections.empty')}
                </div>
              ) : (
                  <VirtualizedColumn
                    items={sections}
                    getKey={(s) => s.id}
                    threshold={50}
                    estimateSize={320}
                    renderItem={(section) => {
                    const sectionTasks = checklists.filter(task => task.section_id === section.id);
                    return (
                      <div
                        className="border border-border/60 rounded-lg overflow-hidden bg-white dark:bg-card"
                      >
                        {/* Section Header */}
                        <div className="bg-slate-200 dark:bg-slate-900 border-b border-border/60 px-4 py-3 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold text-base">{section.name}</h4>
                            {section.is_active === false && (
                              <Badge variant="secondary" className="text-xs">{t('admin.property.archived')}</Badge>
                            )}
                            {canEdit && (
                              <div className="flex items-center gap-1 ml-2">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6"
                                  onClick={() => handleMoveSection(section.id, 'up')}
                                  disabled={isSaving || sections.findIndex(s => s.id === section.id) === 0}
                                >
                                  <ChevronUp className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6"
                                  onClick={() => handleMoveSection(section.id, 'down')}
                                  disabled={isSaving || sections.findIndex(s => s.id === section.id) === sections.length - 1}
                                >
                                  <ChevronDown className="h-4 w-4" />
                                </Button>
                              </div>
                            )}
                          </div>
                          {canEdit && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {section.is_active !== false && (
                                  <>
                                    <DropdownMenuItem onClick={() => openEditSectionDialog(section)}>
                                      <Edit className="h-4 w-4 mr-2" />
                                      {t('admin.property.edit')}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      className="text-destructive"
                                      onClick={() => openDeleteSectionDialog(section)}
                                    >
                                      <X className="h-4 w-4 mr-2" />
                                      {t('admin.property.archive')}
                                    </DropdownMenuItem>
                                  </>
                                )}
                                {section.is_active === false && (
                                  <DropdownMenuItem onClick={() => handleRestoreSection(section)}>
                                    <RotateCcw className="h-4 w-4 mr-2" />
                                    {t('admin.property.restore')}
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>

                        {/* Tasks List */}
                        <div className="divide-y divide-border/60">
                          {sectionTasks.length === 0 ? (
                            <div className="px-4 py-6 text-center text-sm text-muted-foreground">
                              {t('admin.property.sections.noTasks')}
                            </div>
                          ) : (
                            sectionTasks.map((task, index) => (
                              <div
                                key={task.id}
                                className="grid grid-cols-[1fr_200px_80px] items-center gap-4 px-4 py-3 bg-white dark:bg-card"
                              >
                                <div className="font-normal flex items-center gap-2">
                                  <span>{task.name || t('admin.property.noName')}</span>
                                  {task.requires_photo && (
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Camera className="h-4 w-4 text-muted-foreground" />
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          <p>{t('admin.property.photoVerification')}</p>
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  )}
                                </div>
                                <div className="flex justify-start">
                                  <Badge variant="secondary" className="text-xs">
                                    {formatFrequencyDisplay(task)}
                                  </Badge>
                                </div>
                                {canEdit && (
                                  <div className="flex justify-end">
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8"
                                      onClick={() => handleDeleteChecklist(task.id)}
                                    >
                                      <X className="h-4 w-4 text-destructive" />
                                    </Button>
                                  </div>
                                )}
                              </div>
                            ))
                          )}
                        </div>

                        {/* Add Task Button */}
                        {canEdit && (
                          <div className="border-t border-border/60 px-4 py-3 bg-slate-50 dark:bg-slate-950">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openAddTaskDialog(section.id)}
                              className="w-full justify-start text-muted-foreground hover:text-foreground"
                            >
                              <Plus className="h-4 w-4 mr-2" />
                              {t('admin.property.header.addTask')}
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  }}
                  />
              )}
            </CardContent>
          </Card>

          {/* Personnel Panel */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>{t('admin.property.personnel.title')}</CardTitle>
                  <CardDescription>{t('admin.property.personnel.description')}</CardDescription>
                </div>
                {canEdit && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsAssignPersonnelDialogOpen(true);
                      fetchAvailablePersonnel();
                    }}
                    className="flex items-center gap-2"
                  >
                    <User className="h-4 w-4" />
                    {t('admin.property.personnel.assign')}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {isLoadingPersonnel ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              ) : (
                <div className="border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('admin.property.personnel.fullName')}</TableHead>
                        <TableHead>{t('admin.property.personnel.role')}</TableHead>
                        <TableHead>{t('admin.property.status')}</TableHead>
                        {canEdit && <TableHead className="text-right">{t('admin.property.actions')}</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {personnel.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={canEdit ? 4 : 3} className="text-center text-muted-foreground py-8">
                            {t('admin.property.personnel.empty')}
                          </TableCell>
                        </TableRow>
                      ) : (
                        personnel.map((person) => (
                          <TableRow key={person.id}>
                            <TableCell className="font-medium">
                              <Link
                                to={`/workers/${person.id}`}
                                className="text-primary hover:underline"
                              >
                                {person.full_name || t('admin.property.noName')}
                              </Link>
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {person.role === 'owner' ? t('admin.property.personnel.owner') : 
                               person.role === 'coordinator' ? t('admin.property.personnel.coordinator') : 
                               person.role === 'cleaner' ? t('admin.property.personnel.cleaner') : '-'}
                            </TableCell>
                            <TableCell>
                              <Badge
                                className={cn(
                                  getStatusBadgeColor(person.status || ''),
                                  'text-white'
                                )}
                              >
                                {person.status === 'Aktywny' || !person.status ? t('admin.property.personnel.active') : person.status}
                              </Badge>
                            </TableCell>
                            {canEdit && (
                              <TableCell className="text-right">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => setPersonnelToDelete(person.id)}
                                  className="text-destructive hover:text-destructive"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </TableCell>
                            )}
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Add Section Dialog */}
        <Dialog open={isAddSectionDialogOpen} onOpenChange={setIsAddSectionDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('admin.property.sections.add')}</DialogTitle>
              <DialogDescription>
                {t('admin.property.sections.addDescription')}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="section-name">{t('admin.property.sections.name')}</Label>
                <Input
                  id="section-name"
                  value={newSectionName}
                  onChange={(e) => setNewSectionName(e.target.value)}
                  placeholder={t('admin.property.sections.namePlaceholder')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleAddSection();
                    }
                  }}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsAddSectionDialogOpen(false);
                  setNewSectionName('');
                }}
                disabled={isSaving}
              >
                {t('common.cancel')}
              </Button>
              <Button onClick={handleAddSection} disabled={isSaving || !newSectionName.trim()}>
                {isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t('common.saveProgress')}
                  </>
                ) : (
                  t('admin.property.add')
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Section Dialog */}
        <Dialog open={isEditSectionDialogOpen} onOpenChange={setIsEditSectionDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('admin.property.sections.editTitle')}</DialogTitle>
              <DialogDescription>
                {t('admin.property.sections.editDescription')}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-section-name">{t('admin.property.sections.name')}</Label>
                <Input
                  id="edit-section-name"
                  value={newSectionName}
                  onChange={(e) => setNewSectionName(e.target.value)}
                  placeholder={t('admin.property.sections.namePlaceholder')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleUpdateSection();
                    }
                  }}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsEditSectionDialogOpen(false);
                  setEditingSection(null);
                  setNewSectionName('');
                }}
                disabled={isSaving}
              >
                {t('common.cancel')}
              </Button>
              <Button onClick={handleUpdateSection} disabled={isSaving || !newSectionName.trim()}>
                {isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t('common.saveProgress')}
                  </>
                ) : (
                  t('common.save')
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>


        {/* Add Task Dialog */}
        <Dialog open={isAddTaskDialogOpen} onOpenChange={setIsAddTaskDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{t('admin.property.header.addTask')}</DialogTitle>
              <DialogDescription>
                {t('admin.property.tasks.addDescription')}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="task-name">{t('admin.property.tasks.name')}</Label>
                <Input
                  id="task-name"
                  value={newTaskName}
                  onChange={(e) => setNewTaskName(e.target.value)}
                  placeholder={t('admin.property.tasks.namePlaceholder')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && e.target !== e.currentTarget) {
                      handleAddTask();
                    }
                  }}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="task-frequency-type">{t('admin.property.tasks.frequency')}</Label>
                <Select value={newTaskFrequencyType} onValueChange={setNewTaskFrequencyType}>
                  <SelectTrigger id="task-frequency-type">
                    <SelectValue placeholder={t('admin.property.tasks.selectFrequency')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">{t('admin.property.frequency.daily')}</SelectItem>
                    <SelectItem value="work_days">{t('admin.property.frequency.work_days')}</SelectItem>
                    <SelectItem value="specific_days">{t('admin.property.frequency.specific_days')}</SelectItem>
                    <SelectItem value="weekly">{t('admin.property.frequency.weekly')}</SelectItem>
                    <SelectItem value="biweekly">{t('admin.property.frequency.biweekly')}</SelectItem>
                    <SelectItem value="monthly">{t('admin.property.frequency.monthly')}</SelectItem>
                    <SelectItem value="quarterly">{t('admin.property.frequency.quarterly')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Specific Days Toggle Group */}
              {newTaskFrequencyType === 'specific_days' && (
                <div className="space-y-2">
                  <Label>{t('admin.property.tasks.selectDays')}</Label>
                  <ToggleGroup
                    type="multiple"
                    value={selectedDays}
                    onValueChange={setSelectedDays}
                    className="justify-start"
                  >
                    {[0, 1, 2, 3, 4, 5, 6].map((dayIndex) => (
                      <ToggleGroupItem
                        key={dayIndex}
                        value={dayIndex.toString()}
                        aria-label={getDayFullName(dayIndex)}
                      >
                        {getDayLabel(dayIndex)}
                      </ToggleGroupItem>
                    ))}
                  </ToggleGroup>
                  {selectedDays.length === 0 && (
                    <p className="text-sm text-muted-foreground">
                      {t('admin.property.tasks.selectAtLeastOneDay')}
                    </p>
                  )}
                </div>
              )}

              {/* Requires Photo Switch */}
              <div className="flex items-center justify-between space-x-2 pt-2">
                <div className="space-y-0.5">
                  <Label htmlFor="requires-photo" className="text-base font-medium">
                    {t('admin.property.requiresPhoto')}
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {t('admin.property.requiresPhotoHint')}
                  </p>
                </div>
                <Switch
                  id="requires-photo"
                  checked={newTaskRequiresPhoto}
                  onCheckedChange={setNewTaskRequiresPhoto}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsAddTaskDialogOpen(false);
                  setSelectedSectionForTask('');
                  setNewTaskName('');
                  setNewTaskFrequencyType('daily');
                  setSelectedDays([]);
                  setNewTaskRequiresPhoto(false);
                }}
                disabled={isSaving}
              >
                {t('common.cancel')}
              </Button>
              <Button onClick={handleAddTask} disabled={isSaving || !newTaskName.trim()}>
                {isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t('common.saveProgress')}
                  </>
                ) : (
                  t('admin.property.add')
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Assign Personnel Dialog */}
        <Dialog open={isAssignPersonnelDialogOpen} onOpenChange={setIsAssignPersonnelDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('admin.property.personnel.assign')}</DialogTitle>
              <DialogDescription>
                {t('admin.property.personnel.dialogDescription')}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {isLoadingAvailablePersonnel ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              ) : availablePersonnel.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  {t('admin.property.personnel.allAssigned')}
                </p>
              ) : (
                <SearchableSelect
                  options={availablePersonnel.map((person) => ({
                    value: person.id,
                    label: person.full_name || t('admin.property.noName'),
                  }))}
                  value={selectedPersonnelId}
                  onValueChange={setSelectedPersonnelId}
                  placeholder={t('admin.property.personnel.selectPlaceholder')}
                  searchPlaceholder={t('admin.property.personnel.searchPlaceholder')}
                  emptyText={t('admin.property.personnel.emptyAvailable')}
                />
              )}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsAssignPersonnelDialogOpen(false);
                  setSelectedPersonnelId('');
                }}
                disabled={isSaving}
              >
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleAssignPersonnel}
                disabled={isSaving || !selectedPersonnelId}
              >
                {isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t('common.saveProgress')}
                  </>
                ) : (
                  t('admin.property.personnel.assignButton')
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Personnel Access Confirmation */}
        <AlertDialog open={personnelToDelete !== null} onOpenChange={(open) => !open && setPersonnelToDelete(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('admin.property.personnel.removeTitle')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('admin.property.personnel.removeDescription')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isSaving}>{t('common.cancel')}</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  if (personnelToDelete) {
                    handleRemovePersonnelAccess(personnelToDelete);
                  }
                }}
                disabled={isSaving}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t('admin.property.deleting')}
                  </>
                ) : (
                  t('admin.property.personnel.removeAccess')
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Delete Task Confirmation */}
        <AlertDialog open={taskToDelete !== null} onOpenChange={(open) => !open && setTaskToDelete(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('admin.property.tasks.deleteTitle')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('admin.property.tasks.confirmDeleteIrreversible')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isSaving}>{t('common.cancel')}</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteTask}
                disabled={isSaving}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t('admin.property.deleting')}
                  </>
                ) : (
                  t('admin.property.deleteTask')
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <TabsContent value="schedule" forceMount className="mt-6 data-[state=inactive]:hidden">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CalendarIcon className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <CardTitle>{t('admin.property.schedule.title')}</CardTitle>
                    <CardDescription>{t('admin.property.schedule.description')}</CardDescription>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {isLoadingSchedule ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              ) : scheduleChecklists.length === 0 ? (
                <div className="text-center text-muted-foreground py-8">
                  {t('admin.property.schedule.empty')}
                </div>
              ) : (
                <div className="border rounded-lg border-border/60">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-100 dark:bg-slate-900">
                        <TableHead>
                          <button
                            type="button"
                            className="flex items-center gap-1 cursor-pointer hover:text-foreground font-medium"
                            onClick={() => handleScheduleSort('task')}
                          >
                            {t('admin.property.task')}
                            {scheduleSortConfig.key === 'task' && (scheduleSortConfig.direction === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />)}
                          </button>
                        </TableHead>
                        <TableHead>
                          <button
                            type="button"
                            className="flex items-center gap-1 cursor-pointer hover:text-foreground font-medium"
                            onClick={() => handleScheduleSort('section')}
                          >
                            {t('admin.property.schedule.section')}
                            {scheduleSortConfig.key === 'section' && (scheduleSortConfig.direction === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />)}
                          </button>
                        </TableHead>
                        <TableHead>
                          <button
                            type="button"
                            className="flex items-center gap-1 cursor-pointer hover:text-foreground font-medium"
                            onClick={() => handleScheduleSort('frequency')}
                          >
                            {t('admin.property.tasks.frequency')}
                            {scheduleSortConfig.key === 'frequency' && (scheduleSortConfig.direction === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />)}
                          </button>
                        </TableHead>
                        <TableHead>
                          <button
                            type="button"
                            className="flex items-center gap-1 cursor-pointer hover:text-foreground font-medium"
                            onClick={() => handleScheduleSort('nextExecution')}
                          >
                            {t('admin.property.schedule.nextExecution')}
                            {scheduleSortConfig.key === 'nextExecution' && (scheduleSortConfig.direction === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />)}
                          </button>
                        </TableHead>
                        <TableHead>
                          <button
                            type="button"
                            className="flex items-center gap-1 cursor-pointer hover:text-foreground font-medium"
                            onClick={() => handleScheduleSort('status')}
                          >
                            {t('admin.property.status')}
                            {scheduleSortConfig.key === 'status' && (scheduleSortConfig.direction === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />)}
                          </button>
                        </TableHead>
                        <TableHead className="text-right">{t('admin.property.actions')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortedScheduleChecklists.map((task) => {
                        const sectionName = getScheduleSectionName(task);
                        const frequencyText = formatFrequencyDisplay(task);
                        const nextExecution = calculateNextExecution(task);

                        return (
                          <TableRow key={task.id} className="bg-white dark:bg-card">
                            <TableCell className="font-medium">
                              <div className="flex items-center gap-2">
                                <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                                {task.name || t('admin.property.noName')}
                              </div>
                            </TableCell>
                            <TableCell>{sectionName}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Clock className="h-4 w-4 text-muted-foreground" />
                                {frequencyText}
                              </div>
                            </TableCell>
                            <TableCell>{nextExecution}</TableCell>
                            <TableCell>
                              <Badge
                                variant={task.is_active ? 'default' : 'secondary'}
                                className={
                                  task.is_active
                                    ? 'bg-green-500 text-white hover:bg-green-600'
                                    : 'bg-slate-400 text-white'
                                }
                              >
                                {task.is_active ? t('admin.property.schedule.active') : t('admin.property.schedule.inactive')}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-8 w-8">
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem
                                    onClick={() => handleToggleTaskStatus(task)}
                                  >
                                    {task.is_active ? (
                                      <>
                                        <Pause className="h-4 w-4 mr-2" />
                                        {t('admin.property.schedule.pause')}
                                      </>
                                    ) : (
                                      <>
                                        <Play className="h-4 w-4 mr-2" />
                                        {t('admin.property.schedule.activate')}
                                      </>
                                    )}
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleGenerateNow(task)}>
                                    <Zap className="h-4 w-4 mr-2" />
                                    {t('admin.property.schedule.generateNow')}
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Add Single Task Dialog */}
        <Dialog open={isAddSingleTaskDialogOpen} onOpenChange={setIsAddSingleTaskDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('admin.property.tasks.singleTitle')}</DialogTitle>
              <DialogDescription>
                {t('admin.property.tasks.singleDescription')}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="single-task-section">{t('admin.property.tasks.sectionRequired')}</Label>
                <Select value={singleTaskSectionId} onValueChange={setSingleTaskSectionId}>
                  <SelectTrigger id="single-task-section">
                    <SelectValue placeholder={t('admin.property.tasks.selectSection')} />
                  </SelectTrigger>
                  <SelectContent>
                    {sections.map((section) => (
                      <SelectItem key={section.id} value={section.id}>
                        {section.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="single-task-name">{t('admin.property.tasks.nameRequiredMark')}</Label>
                <Input
                  id="single-task-name"
                  value={singleTaskName}
                  onChange={(e) => setSingleTaskName(e.target.value)}
                  placeholder={t('admin.property.tasks.singlePlaceholder')}
                />
              </div>

              {/* Requires Photo Switch */}
              <div className="flex items-center justify-between space-x-2 pt-2">
                <div className="space-y-0.5">
                  <Label htmlFor="single-task-requires-photo" className="text-base font-medium">
                    {t('admin.property.requiresPhoto')}
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {t('admin.property.requiresPhotoHint')}
                  </p>
                </div>
                <Switch
                  id="single-task-requires-photo"
                  checked={singleTaskRequiresPhoto}
                  onCheckedChange={setSingleTaskRequiresPhoto}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsAddSingleTaskDialogOpen(false);
                  setSingleTaskSectionId('');
                  setSingleTaskName('');
                  setSingleTaskRequiresPhoto(false);
                }}
                disabled={isSaving}
              >
                {t('common.cancel')}
              </Button>
              <Button onClick={handleAddSingleTask} disabled={isSaving || !singleTaskSectionId || !singleTaskName.trim()}>
                {isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t('common.saveProgress')}
                  </>
                ) : (
                  t('admin.property.header.addTask')
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <TabsContent value="warehouse" forceMount className="mt-6 space-y-6 data-[state=inactive]:hidden">
          {/* Inventory Section */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>{t('admin.property.warehouse.assortmentTitle')}</CardTitle>
                  <CardDescription>
                    {t('admin.property.warehouse.assortmentDescription')}
                  </CardDescription>
                </div>
                {canEdit && (
                  <Button
                    onClick={() => {
                      fetchCatalog();
                      setIsAddInventoryDialogOpen(true);
                    }}
                    className="flex items-center gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    {t('admin.property.warehouse.addFromCatalog')}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {isLoadingInventory ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              ) : inventoryItems.length === 0 ? (
                <div className="text-center text-muted-foreground py-8">
                  {t('admin.property.warehouse.emptyAssortment')}
                </div>
              ) : (
                <div className="border border-border/60 rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-200 dark:bg-slate-800">
                        <TableHead>{t('admin.property.warehouse.productName')}</TableHead>
                        <TableHead>{t('admin.property.warehouse.unit')}</TableHead>
                        {canEdit && <TableHead className="w-[100px]">{t('admin.property.actions')}</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {inventoryItems.map((item) => {
                        const displayName = item.cleaning_catalog?.name || item.item_name;
                        const displayUnit = item.cleaning_catalog?.unit || item.unit || t('admin.property.unitPiece');
                        return (
                          <TableRow key={item.id}>
                            <TableCell className="font-medium">{displayName}</TableCell>
                            <TableCell>{displayUnit}</TableCell>
                            {canEdit && (
                              <TableCell>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteInventoryItem(item.id)}
                                  disabled={isDeletingInventory === item.id}
                                >
                                  {isDeletingInventory === item.id ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Trash2 className="h-4 w-4 text-destructive" />
                                  )}
                                </Button>
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Orders Section */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>{t('admin.property.warehouse.ordersTitle')}</CardTitle>
                  <CardDescription>
                    {t('admin.property.warehouse.ordersDescription')}
                  </CardDescription>
                </div>
                {canEdit && (
                  <Button
                    onClick={() => setIsOrderDialogOpen(true)}
                    className="flex items-center gap-2"
                  >
                    <Package className="h-4 w-4" />
                    {t('admin.property.warehouse.orderProducts')}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {isLoadingMaterialRequests ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              ) : materialRequests.length === 0 ? (
                <div className="text-center text-muted-foreground py-8">
                  {t('admin.property.warehouse.noOrders')}
                </div>
              ) : (
                <div className="border border-border/60 rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-200 dark:bg-slate-800">
                        <TableHead>{t('admin.property.warehouse.products')}</TableHead>
                        <TableHead>{t('admin.property.warehouse.requester')}</TableHead>
                        <TableHead>{t('admin.property.warehouse.orderDate')}</TableHead>
                        <TableHead>{t('admin.property.status')}</TableHead>
                        {canEdit && <TableHead>{t('admin.property.action')}</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {materialRequests.map((request) => {
                        // Parse items JSONB – name: name/product_name/label/item_name, quantity: quantity/qty/amount/requested_qty
                        const items = (() => {
                          try {
                            if (!request.items) return [];
                            const raw = Array.isArray(request.items) ? request.items : [request.items];
                            return raw.map((item: any) => {
                              if (typeof item === 'string') return { name: item, quantity: 0, unit: '' };
                              const name = item.name || item.product_name || item.label || item.item_name || t('admin.property.unknownProduct');
                              const quantity = item.quantity ?? item.qty ?? item.amount ?? item.requested_qty ?? 0;
                              const unit = item.unit || '';
                              return { name, quantity, unit };
                            });
                          } catch (e) {
                            console.error('Error parsing items:', e);
                            return [];
                          }
                        })();

                        const requestNotes = request.notes?.trim?.() || null;

                        const prof = request.profiles as { full_name?: string } | { full_name?: string }[] | null;
                        const requesterName = Array.isArray(prof) ? (prof[0]?.full_name ?? null) : (prof?.full_name ?? null);
                        const requesterDisplay = requesterName ?? (request.requester_id ? t('common.unknown') : '—');

                        return (
                          <TableRow key={request.id}>
                            <TableCell className="font-medium">
                              <div>
                                {items.length === 0 ? (
                                  <span className="text-muted-foreground">{t('admin.property.warehouse.noProducts')}</span>
                                ) : (
                                  items.map((item, index) => (
                                    <div key={index} className="flex justify-between items-center text-sm gap-2 py-0.5">
                                      <span className="font-medium">{item.name}</span>
                                      <span className="text-muted-foreground whitespace-nowrap">
                                        x{item.quantity}{item.unit ? ` ${item.unit}` : ''}
                                      </span>
                                    </div>
                                  ))
                                )}
                                {requestNotes && (
                                  <p className="text-sm text-muted-foreground mt-1">
                                    {t('admin.property.comment')} {requestNotes}
                                  </p>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {requesterDisplay}
                            </TableCell>
                            <TableCell>
                              {new Date(request.created_at).toLocaleDateString(intlLocale, {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                              })}
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant={
                                  request.status === 'delivered'
                                    ? 'default'
                                    : request.status === 'pending'
                                    ? 'outline'
                                    : 'secondary'
                                }
                                className={
                                  request.status === 'delivered'
                                    ? 'bg-green-500'
                                    : request.status === 'pending'
                                    ? ''
                                    : ''
                                }
                              >
                                {request.status === 'delivered'
                                  ? t('admin.property.warehouse.delivered')
                                  : request.status === 'pending'
                                  ? t('admin.property.taskStatus.pending')
                                  : request.status === 'approved'
                                  ? t('admin.property.warehouse.approved')
                                  : request.status || t('common.unknown')}
                              </Badge>
                            </TableCell>
                            {canEdit && (
                              <TableCell>
                                {request.status !== 'delivered' && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleMarkAsDelivered(request.id)}
                                  >
                                    {t('admin.property.warehouse.approveDelivery')}
                                  </Button>
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* History Section
              AUDYT DANYCH (Magazyn i Historia):
              1. Źródło: tabela cleaning_tasks (z joinami: profiles, property_sections).
              2. Filtry: location_id = nieruchomość, org_id = bieżąca org, status = 'done';
                 opcjonalnie: completed_at w zakresie historyDateRange, assigned_staff_id = historyStaffFilter.
              3. Opis zadania: nie był wyświetlany w osobnym polu. Dostępny w danych:
                 - cleaning_tasks.coordinator_notes (opis od koordynatora),
                 - cleaning_tasks.checklist (JSONB) – pierwszy element może mieć label/name/task_name.
              Kolumna "Opis Zadania" poniżej wykorzystuje te dane.
          */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>{t('admin.property.history.title')}</CardTitle>
                  <CardDescription>
                    {t('admin.property.history.description')}
                  </CardDescription>
                </div>
                <Button
                  onClick={() => {
                    // TODO: Implement generate report
                    toast.info(t('admin.property.history.reportSoon'));
                  }}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <FileText className="h-4 w-4" />
                  {t('admin.property.history.generateReport')}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Filters */}
              <div className="flex flex-wrap items-center gap-4 mb-6 pb-4 border-b">
                <div className="flex items-center gap-2">
                  <Label htmlFor="history-date-range" className="whitespace-nowrap">
                    {t('admin.property.dateRange')}
                  </Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        id="history-date-range"
                        variant="outline"
                        className={cn(
                          "w-[280px] justify-start text-left font-normal",
                          !historyDateRange.from && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {historyDateRange.from ? (
                          historyDateRange.to ? (
                            <>
                              {format(historyDateRange.from, "dd MMM yyyy", { locale: dateLocale })} -{" "}
                              {format(historyDateRange.to, "dd MMM yyyy", { locale: dateLocale })}
                            </>
                          ) : (
                            format(historyDateRange.from, "dd MMM yyyy", { locale: dateLocale })
                          )
                        ) : (
                          <span>{t('admin.property.selectDateRange')}</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        initialFocus
                        mode="range"
                        defaultMonth={historyDateRange.from}
                        selected={{
                          from: historyDateRange.from,
                          to: historyDateRange.to,
                        }}
                        onSelect={(range) => {
                          setHistoryDateRange({
                            from: range?.from,
                            to: range?.to,
                          });
                        }}
                        numberOfMonths={2}
                        locale={dateLocale}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="flex items-center gap-2">
                  <Label htmlFor="history-staff-filter" className="whitespace-nowrap">
                    {t('admin.property.history.worker')}
                  </Label>
                  <Select value={historyStaffFilter} onValueChange={setHistoryStaffFilter}>
                    <SelectTrigger id="history-staff-filter" className="w-[200px]">
                      <SelectValue placeholder={t('admin.property.history.allWorkers')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('admin.property.history.allWorkers')}</SelectItem>
                      {personnel.map((person) => (
                        <SelectItem key={person.id} value={person.id}>
                          {person.full_name || t('admin.property.noName')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {(historyDateRange.from || historyDateRange.to || historyStaffFilter !== 'all') && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setHistoryDateRange({});
                      setHistoryStaffFilter('all');
                    }}
                  >
                    {t('admin.property.clearFilters')}
                  </Button>
                )}
              </div>

              {isLoadingCompletedTasks ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              ) : completedTasks.length === 0 ? (
                <div className="text-center text-muted-foreground py-8">
                  {t('admin.property.history.empty')}
                </div>
              ) : (
                <div className="border border-border/60 rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-200 dark:bg-slate-800">
                        <TableHead>{t('admin.property.history.datetime')}</TableHead>
                        <TableHead>{t('admin.property.history.workerCol')}</TableHead>
                        <TableHead>{t('admin.property.history.taskSection')}</TableHead>
                        <TableHead>{t('admin.property.history.context')}</TableHead>
                        <TableHead>{t('admin.property.status')}</TableHead>
                        <TableHead>{t('admin.property.history.notes')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {completedTasks.map((task) => {
                        const profileJoin = Array.isArray(task.profiles) ? task.profiles[0] : task.profiles;
                        const sectionJoin = Array.isArray(task.property_sections) ? task.property_sections[0] : task.property_sections;
                        const staffName = task.performerName ?? profileJoin?.full_name ?? t('common.unknown');
                        const sectionName = sectionJoin?.name || t('admin.property.missingSection');
                        const taskDescription = (() => {
                          if (task.coordinator_notes?.trim()) return task.coordinator_notes.trim();
                          const firstItem = parseChecklistItems(task.checklist)[0];
                          const label = firstItem?.name || firstItem?.task_name;
                          if (label?.trim()) return label.trim();
                          return '';
                        })();
                        const contextDisplay = taskDescription || t('admin.property.generalTask');
                        const taskStatus = task.status === 'done' ? t('admin.property.taskStatus.done') : 
                                         task.status === 'cancelled' ? t('admin.property.taskStatus.cancelled') : 
                                         task.status === 'in_progress' ? t('admin.property.taskStatus.inProgress') : 
                                         task.status === 'pending' ? t('admin.property.taskStatus.pending') : t('common.unknown');

                        return (
                          <TableRow key={task.id}>
                            <TableCell>
                              {task.completed_at
                                ? new Date(task.completed_at).toLocaleString(intlLocale, {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })
                                : t('admin.property.noDate')}
                            </TableCell>
                            <TableCell>{staffName}</TableCell>
                            <TableCell>
                              <div className="space-y-1">
                                <div className="font-medium">{sectionName}</div>
                                {task.coordinator_notes && (
                                  <div className="text-xs text-muted-foreground">
                                    {task.coordinator_notes}
                                  </div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="max-w-[220px]">
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div
                                      className={cn(
                                        'truncate cursor-default',
                                        !taskDescription ? 'text-muted-foreground' : ''
                                      )}
                                      title={contextDisplay}
                                    >
                                      {contextDisplay}
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent side="top" className="max-w-sm whitespace-pre-wrap break-words">
                                    {contextDisplay}
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant={
                                  task.status === 'done'
                                    ? 'default'
                                    : task.status === 'cancelled'
                                    ? 'destructive'
                                    : 'secondary'
                                }
                                className={
                                  task.status === 'done'
                                    ? 'bg-green-500'
                                    : task.status === 'cancelled'
                                    ? 'bg-red-500'
                                    : ''
                                }
                              >
                                {taskStatus}
                              </Badge>
                            </TableCell>
                            <TableCell className="max-w-xs">
                              <div className="truncate" title={task.staff_notes || ''}>
                                {task.staff_notes || t('admin.property.noNotes')}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  {historyHasMore && (
                    <div className="flex justify-center pt-4 pb-2">
                      <Button
                        variant="outline"
                        onClick={() => {
                          const nextPage = historyPage + 1;
                          setHistoryPage(nextPage);
                          fetchCompletedTasks(nextPage, true);
                        }}
                        disabled={isLoadingMoreHistory}
                      >
                        {isLoadingMoreHistory ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : null}
                        {t('admin.property.showMore')}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Historia zgłoszonych usterek */}
          <Card>
            <CardHeader>
              <CardTitle>{t('admin.property.history.issuesTitle')}</CardTitle>
              <CardDescription>
                {t('admin.property.history.issuesDescription')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap items-center gap-4 mb-6 pb-4 border-b">
                <div className="flex items-center gap-2">
                  <Label htmlFor="issues-history-date-range" className="whitespace-nowrap">
                    {t('admin.property.dateRange')}
                  </Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        id="issues-history-date-range"
                        variant="outline"
                        className={cn(
                          "w-[280px] justify-start text-left font-normal",
                          !issuesHistoryDateRange.from && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {issuesHistoryDateRange.from ? (
                          issuesHistoryDateRange.to ? (
                            <>
                              {format(issuesHistoryDateRange.from, "dd MMM yyyy", { locale: dateLocale })} -{" "}
                              {format(issuesHistoryDateRange.to, "dd MMM yyyy", { locale: dateLocale })}
                            </>
                          ) : (
                            format(issuesHistoryDateRange.from, "dd MMM yyyy", { locale: dateLocale })
                          )
                        ) : (
                          <span>{t('admin.property.selectDateRange')}</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        initialFocus
                        mode="range"
                        defaultMonth={issuesHistoryDateRange.from}
                        selected={{
                          from: issuesHistoryDateRange.from,
                          to: issuesHistoryDateRange.to,
                        }}
                        onSelect={(range) => {
                          setIssuesHistoryDateRange({
                            from: range?.from,
                            to: range?.to,
                          });
                        }}
                        numberOfMonths={2}
                        locale={dateLocale}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                {(issuesHistoryDateRange.from || issuesHistoryDateRange.to) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIssuesHistoryDateRange({})}
                  >
                    {t('admin.property.clearFilters')}
                  </Button>
                )}
              </div>

              {isLoadingIssuesHistory ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              ) : issuesHistory.length === 0 ? (
                <div className="text-center text-muted-foreground py-8">
                  {t('admin.property.history.issuesEmpty')}
                </div>
              ) : (
                <CleaningIssueHistoryTable
                  issues={issuesHistory}
                  canEdit={canEdit}
                  onResolved={fetchIssuesHistory}
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Holidays Tab */}
        <TabsContent value="holidays" forceMount className="mt-6 space-y-6 data-[state=inactive]:hidden">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>{t('admin.property.holidays.title')}</CardTitle>
                  <CardDescription>
                    {t('admin.property.holidays.description')}
                  </CardDescription>
                </div>
                {canEdit && (
                  <Button
                    onClick={() => setIsAddHolidayDialogOpen(true)}
                    className="flex items-center gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    {t('admin.property.holidays.add')}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {isLoadingHolidays ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              ) : holidays.length === 0 ? (
                <div className="text-center text-muted-foreground py-8">
                  {t('admin.property.holidays.empty')}
                </div>
              ) : (
                <div className="border border-border/60 rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-200 dark:bg-slate-800">
                        <TableHead>{t('admin.property.holidays.date')}</TableHead>
                        <TableHead>{t('admin.property.holidays.descriptionCol')}</TableHead>
                        <TableHead>{t('admin.property.holidays.scope')}</TableHead>
                        {canEdit && <TableHead className="w-[100px]">{t('admin.property.actions')}</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {holidays.map((holiday) => {
                        const holidayDate = new Date(holiday.holiday_date);
                        const isPast = holidayDate < new Date();
                        const isToday = holidayDate.toDateString() === new Date().toDateString();
                        
                        return (
                          <TableRow
                            key={holiday.id}
                            className={cn(
                              isPast && !isToday && 'opacity-60',
                              isToday && 'bg-blue-50 dark:bg-blue-950/20'
                            )}
                          >
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                                <span className="font-medium">
                                  {format(holidayDate, 'dd.MM.yyyy', { locale: dateLocale })}
                                </span>
                                {isToday && (
                                  <Badge variant="secondary" className="text-xs">
                                    {t('admin.property.today')}
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              {holiday.description || (
                                <span className="text-muted-foreground italic">{t('admin.property.noDescription')}</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {holiday.location_id ? (
                                <Badge variant="outline">{t('admin.property.holidays.thisProperty')}</Badge>
                              ) : (
                                <Badge variant="default">{t('admin.property.holidays.wholeOrg')}</Badge>
                              )}
                            </TableCell>
                            {canEdit && (
                              <TableCell>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setHolidayToDelete(holiday.id)}
                                  className="text-destructive hover:text-destructive"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Task Details Dialog */}
      <Dialog open={isTaskDetailsDialogOpen} onOpenChange={setIsTaskDetailsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('admin.property.taskDetails.title')}</DialogTitle>
            <DialogDescription>
              {t('admin.property.taskDetails.description')}
            </DialogDescription>
          </DialogHeader>
          {selectedTaskDetails && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">{t('admin.property.status')}</Label>
                  <div className="mt-1">
                    {getTaskStatusBadge(getEffectiveTaskStatus(selectedTaskDetails))}
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">{t('admin.property.taskDetails.type')}</Label>
                  <div className="mt-1 flex items-center gap-2">
                    {selectedTaskDetails.task_type === 'coordinator_single' && (
                      <Zap className="h-4 w-4 text-yellow-600 dark:text-yellow-400 shrink-0" />
                    )}
                    <span className="text-sm">{getTaskTypeLabel(selectedTaskDetails.task_type)}</span>
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">{t('admin.property.taskDetails.scheduled')}</Label>
                  <div className="mt-1 text-sm">
                    {new Date(selectedTaskDetails.scheduled_at).toLocaleDateString(intlLocale, {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </div>
                </div>
                {getEffectiveTaskStatus(selectedTaskDetails) === 'done' && (
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">{t('admin.property.taskDetails.checkedBy')}</Label>
                    <div className="mt-1 text-sm">
                      {taskDetailsPerformerName || selectedTaskDetails.assignedStaffName || '—'}
                    </div>
                  </div>
                )}
              </div>
              
              {selectedTaskDetails.coordinator_notes && (
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">{t('admin.property.instructions.coordinatorNotes')}</Label>
                  <div className="mt-1 p-3 bg-slate-200/50 dark:bg-slate-900/50 border border-border/60 rounded-lg">
                    <p className="text-sm">{selectedTaskDetails.coordinator_notes}</p>
                  </div>
                </div>
              )}
              
              {selectedTaskDetails.staff_notes && (
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">{t('admin.property.taskDetails.staffNotes')}</Label>
                  <div className="mt-1 p-3 bg-slate-200/50 dark:bg-slate-900/50 border border-border/60 rounded-lg">
                    <p className="text-sm">{selectedTaskDetails.staff_notes}</p>
                  </div>
                </div>
              )}

              {selectedTaskDetails.started_at && (
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">{t('admin.property.taskDetails.started')}</Label>
                  <div className="mt-1 text-sm">
                    {new Date(selectedTaskDetails.started_at).toLocaleDateString(intlLocale, {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </div>
                </div>
              )}

              {selectedTaskDetails.completed_at && (
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">{t('admin.property.taskDetails.finished')}</Label>
                  <div className="mt-1 text-sm">
                    {new Date(selectedTaskDetails.completed_at).toLocaleDateString(intlLocale, {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setIsTaskDetailsDialogOpen(false)}>{t('admin.property.close')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Order Products Dialog */}
      <Dialog open={isOrderDialogOpen} onOpenChange={setIsOrderDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('admin.property.warehouse.orderProducts')}</DialogTitle>
            <DialogDescription>
              {t('admin.property.warehouse.orderDialogDescription')}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="order-product-select">{t('admin.property.warehouse.product')}</Label>
              <Select value={selectedProductId} onValueChange={setSelectedProductId}>
                <SelectTrigger id="order-product-select">
                  <SelectValue placeholder={t('admin.property.warehouse.selectProduct')} />
                </SelectTrigger>
                <SelectContent>
                  {inventoryItems.length === 0 ? (
                    <SelectItem value="no-products" disabled>
                      {t('admin.property.warehouse.noProductsInAssortment')}
                    </SelectItem>
                  ) : (
                    inventoryItems.map((item) => {
                      const displayName = item.cleaning_catalog?.name || item.item_name;
                      return (
                        <SelectItem key={item.id} value={item.id}>
                          {displayName}
                        </SelectItem>
                      );
                    })
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="order-quantity">{t('admin.property.warehouse.quantity')}</Label>
              <Input
                id="order-quantity"
                type="number"
                min="1"
                value={orderQuantity}
                onChange={(e) => setOrderQuantity(e.target.value)}
                placeholder="1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsOrderDialogOpen(false);
                setSelectedProductId('');
                setOrderQuantity('1');
              }}
              disabled={isSubmittingOrder}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleSubmitOrder}
              disabled={isSubmittingOrder || !selectedProductId || !orderQuantity}
            >
              {isSubmittingOrder ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('admin.property.sending')}
                </>
              ) : (
                t('admin.property.warehouse.sendRequest')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Inventory Item Dialog */}
      <Dialog open={isAddInventoryDialogOpen} onOpenChange={setIsAddInventoryDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('admin.property.warehouse.addFromCatalog')}</DialogTitle>
            <DialogDescription>
              {t('admin.property.warehouse.catalogDialogDescription')}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="catalog-product-select">{t('admin.property.warehouse.catalogProduct')}</Label>
              <Select value={selectedCatalogItemId} onValueChange={setSelectedCatalogItemId}>
                <SelectTrigger id="catalog-product-select">
                  <SelectValue placeholder={t('admin.property.warehouse.selectCatalogProduct')} />
                </SelectTrigger>
                <SelectContent>
                  {catalogItems.length === 0 ? (
                    <SelectItem value="no-products" disabled>
                      {t('admin.property.warehouse.catalogEmpty')}
                    </SelectItem>
                  ) : (
                    catalogItems
                      .filter(catalogItem => 
                        !inventoryItems.some(invItem => invItem.catalog_item_id === catalogItem.id)
                      )
                      .map((item) => (
                        <SelectItem key={item.id} value={item.id}>
                          {item.name} ({item.unit})
                        </SelectItem>
                      ))
                  )}
                </SelectContent>
              </Select>
              {catalogItems.length > 0 && 
               catalogItems.filter(catalogItem => 
                 !inventoryItems.some(invItem => invItem.catalog_item_id === catalogItem.id)
               ).length === 0 && (
                <p className="text-sm text-muted-foreground mt-2">
                  {t('admin.property.warehouse.allInAssortment')}
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsAddInventoryDialogOpen(false);
                setSelectedCatalogItemId('');
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleAddInventoryItem}
              disabled={!selectedCatalogItemId}
            >
              {t('admin.property.warehouse.addToAssortment')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QR Code Management Dialog */}
      <Dialog open={isQRManagementDialogOpen} onOpenChange={setIsQRManagementDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader className="print:hidden">
            <DialogTitle>{t('admin.property.qr.dialogTitle')}</DialogTitle>
            <DialogDescription>
              {t('admin.property.qr.dialogDescription')}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-6">
            {location?.qr_code_token ? (
              <>
                {/* Print-only content */}
                <div className="print-content hidden print:flex">
                  <div className="flex flex-col items-center justify-center space-y-6 w-full">
                    {/* Logo */}
                    <div className="text-center">
                      <h1 className="text-3xl font-bold text-black">DOMIO Cleaning</h1>
                    </div>
                    
                    {/* QR Code */}
                    <div className="bg-white p-6 rounded-lg" id="qr-code-container">
                      <QRCodeSVG
                        value={`https://cleaning.domio.com.pl/check-in/${location.qr_code_token}`}
                        size={300}
                        level="H"
                        includeMargin={true}
                      />
                    </div>
                    
                    {/* Property Name */}
                    <div className="text-center space-y-2">
                      <p className="text-xl font-semibold text-black">{location.address}</p>
                    </div>
                    
                    {/* Instruction */}
                    <div className="text-center">
                      <p className="text-base text-gray-700">
                        {t('admin.property.qr.scan')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Screen content */}
                <div className="flex flex-col items-center justify-center space-y-4 print:hidden">
                  <div className="bg-white p-6 rounded-lg border-2 border-gray-300">
                    <QRCodeSVG
                      value={`https://cleaning.domio.com.pl/check-in/${location.qr_code_token}`}
                      size={256}
                      level="H"
                      includeMargin={true}
                    />
                  </div>
                  <div className="text-center space-y-2">
                    <p className="text-sm font-medium">{t('admin.property.qr.propertyName')}</p>
                    <p className="text-lg font-semibold">{location.address}</p>
                    <p className="text-xs text-muted-foreground mt-4">
                      {t('admin.property.qr.scan')}
                    </p>
                  </div>
                </div>
                <div className="flex flex-col gap-3 print:hidden">
                  <Button
                    onClick={handleRegenerateQRToken}
                    variant="outline"
                    disabled={isRegeneratingQR || !canEdit}
                    className="w-full"
                  >
                    {isRegeneratingQR ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t('admin.property.generating')}
                      </>
                    ) : (
                      <>
                        <RefreshCw className="mr-2 h-4 w-4" />
                        {t('admin.property.qr.generateNew')}
                      </>
                    )}
                  </Button>
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 text-sm text-yellow-800 dark:text-yellow-200">
                    <strong>{t('admin.property.warning')}</strong> {t('admin.property.qr.regenerateWarning')}
                  </div>
                  <Button
                    onClick={handlePrintQR}
                    className="w-full"
                  >
                    <Printer className="mr-2 h-4 w-4" />
                    {t('admin.property.qr.print')}
                  </Button>
                </div>
              </>
            ) : (
              <div className="text-center py-8 space-y-4 print:hidden">
                <p className="text-muted-foreground">{t('admin.property.qr.noQr')}</p>
                {canEdit && (
                  <Button
                    onClick={handleRegenerateQRToken}
                    disabled={isRegeneratingQR}
                  >
                    {isRegeneratingQR ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t('admin.property.generating')}
                      </>
                    ) : (
                      <>
                        <QrCode className="mr-2 h-4 w-4" />
                        {t('admin.property.qr.generate')}
                      </>
                    )}
                  </Button>
                )}
              </div>
            )}
          </div>
          <DialogFooter className="print:hidden">
            <Button onClick={() => setIsQRManagementDialogOpen(false)}>{t('admin.property.close')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Holiday Dialog */}
      <Dialog open={isAddHolidayDialogOpen} onOpenChange={setIsAddHolidayDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('admin.property.holidays.add')}</DialogTitle>
            <DialogDescription>
              {t('admin.property.holidays.dialogDescription')}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="holiday-date">{t('admin.property.holidays.date')}</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    id="holiday-date"
                    variant="outline"
                    className={cn(
                      'w-full justify-start text-left font-normal',
                      !newHolidayDate && 'text-muted-foreground'
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {newHolidayDate ? (
                      format(newHolidayDate, 'dd.MM.yyyy', { locale: dateLocale })
                    ) : (
                      <span>{t('admin.property.holidays.selectDate')}</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={newHolidayDate}
                    onSelect={setNewHolidayDate}
                    initialFocus
                    locale={dateLocale}
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <Label htmlFor="holiday-description">{t('admin.property.holidays.descriptionOptional')}</Label>
              <Input
                id="holiday-description"
                placeholder={t('admin.property.holidays.descriptionPlaceholder')}
                value={newHolidayDescription}
                onChange={(e) => setNewHolidayDescription(e.target.value)}
              />
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="global-holiday"
                checked={isGlobalHoliday}
                onCheckedChange={setIsGlobalHoliday}
              />
              <Label htmlFor="global-holiday" className="cursor-pointer">
                {t('admin.property.holidays.applyAll')}
              </Label>
            </div>
            {isGlobalHoliday && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-sm text-blue-800 dark:text-blue-200">
                <strong>{t('admin.property.warning')}</strong> {t('admin.property.holidays.globalWarning')}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsAddHolidayDialogOpen(false);
                setNewHolidayDate(undefined);
                setNewHolidayDescription('');
                setIsGlobalHoliday(false);
              }}
              disabled={isSaving}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleAddHoliday}
              disabled={isSaving || !newHolidayDate}
            >
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('common.saveProgress')}
                </>
              ) : (
                t('admin.property.holidays.add')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Holiday Confirmation Dialog */}
      <AlertDialog open={!!holidayToDelete} onOpenChange={(open) => !open && setHolidayToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('admin.property.holidays.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('admin.property.holidays.deleteDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setHolidayToDelete(null)}>
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (holidayToDelete) {
                  handleDeleteHoliday(holidayToDelete);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('admin.property.deleting')}
                </>
              ) : (
                t('admin.property.delete')
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {currentOrgId && location && (
        <ExtraPaidJobModal
          open={isExtraPaidOpen}
          onOpenChange={setIsExtraPaidOpen}
          orgId={currentOrgId}
          locations={[{ id: location.id, address: location.address || t('admin.property.fallbackObject') }]}
          presetLocationId={location.id}
        />
      )}
    </div>
  );
};
